import { getBackendBoolean, nullIfBackendEmptyId } from '@Sage/ssm-utils/backendScalars';
import { randomId } from 'utils/numbers';
import { getLiteral } from 'utils/getLiteral';
import { successToast, warningToast } from 'utils/toast';
import { PRODUCTS, SALESORDERS, SALESORDERSLINES } from 'constants/Entities';
import { formatSelectionFilters } from 'domain/FilterApiWrapper';
import { SalesOrdersModel } from 'models';
import Context from 'managers/Context';
import { FuzzyMap } from 'utils/fuzzy';
import { isEmpty } from 'utils/isEmpty';
import { getDifferentDay } from 'utils/dates';
import { getSrcProductCircleAvatar } from 'utils/getSrcAvatar';
import { getCurrency } from 'utils/currency';
import {
    formatDecimalByConfig,
    getDecimalsByConfig,
    roundDecimalByConfig,
    DECIMAL_TYPES,
} from 'utils/decimal';
import { getLegacyFormErrors } from 'utils/legacyCrudValidation';
import { getActiveCrud, getCustomizeCrudAction } from 'utils/crud';
import { getGroupByParentValue } from 'utils/getGroupByParentValue';
import { translateFollowingItemForBackend } from 'utils/filters';
import { isSageImplementation } from 'utils/sage';
import {
    applySalesOrderLinesSmartFields,
    clearLineSmartFieldsOnHeaderChange,
    formatLineDateForDisplay,
    formatLineDateForServer,
    getEntityExtraFieldSchemaConfig,
    getSmartFieldProps,
    isStandardFieldHidden,
    isLineFieldMandatory,
    isLineFieldValueEmpty,
    normalizeLineDateValue,
    resolveLineFieldValue,
} from 'utils/fm/fields';
import { findProductUnitRelationByStoredUomField } from '@Sage/ssm-utils';

function emptyFilterRate(item) {
    return !item.isDisabled;
}

export default class OrdersManager {
    canAsyncExtraFields() {
        return false;
    }

    beforeRenderSalesOrderDiscountsSection = (section) => {
        const state = Context.store.getState();
        const schema = state?.config?.standardFieldsSchemaMap?.[SALESORDERS.extraFieldName];
        const activeCrud = getActiveCrud(state);
        const data = activeCrud?.data || {};
        const maxTiers = isSageImplementation(state.config?.productModeCode) ? 1 : 4;
        const allHidden = Array.from({ length: maxTiers }, (_, i) => `discount${i + 1}`).every(
            (fieldId) => {
                const fieldSchema = schema?.[fieldId];
                const { defaultValue: _defaultValue, ...fieldOverrides } = getSmartFieldProps({
                    field: fieldSchema,
                    configMap: activeCrud?.configMap,
                    data,
                    isNewEntity: !data.id,
                });
                return isStandardFieldHidden(fieldSchema, fieldOverrides);
            },
        );

        if (allHidden) {
            section.show = false;
            section.fields = section.fields.map((field) => ({ ...field, hidden: true }));
        }

        return section;
    };

    getSchema = (defaultInputSchema) => {
        const state = Context.store.getState();
        const quoteProductByEnvironment = state?.config?.permission?.quoteProductByEnvironment;
        const salesOrderManualPrices = state?.config?.userData?.salesOrderManualPrices;

        const schema = [
            {
                title: getLiteral('label_salesorder_details'),
                show: true,
                columns: 2,
                fields: [
                    {
                        ...defaultInputSchema,
                        id: 'idBranch',
                        serverId: 'idBranch',
                        fieldConfiguration: 'idSucursal',
                        preserveInPayload: true,
                        type: 'singleValueList',
                        label: getLiteral('label_environment'),
                        description: getLiteral('helptext_environment_desc'),
                        hasDefaultUserValues: true,
                        inputAttrs: {
                            list: 'tblSucursales',
                        },
                        column: 1,
                        hiddenForBulk: true,
                        isMultiEnvironmentField: true,
                    },
                    {
                        ...defaultInputSchema,
                        id: 'reference',
                        serverId: 'reference',
                        fieldConfiguration: 'reference',
                        type: 'text',
                        label: getLiteral('label_reference_salesorders'),
                        column: 2,
                    },
                    {
                        ...defaultInputSchema,
                        id: 'idCompany',
                        serverId: 'idCompany',
                        fieldConfiguration: 'idEmpresa',
                        type: 'fuzzySearchSingle',
                        label: getLiteral('label_account'),
                        hint: getLiteral('action_searchcompany'),
                        warningField: {
                            dataField: 'companyWarning',
                            permission: 'viewWarningsInSalesOrders',
                        },
                        inputAttrs: {
                            list: 'companyrates',
                            field: 'companyrates',
                            extraValueField: 'companyExtraInfo',
                            excludeDisabledStates: true,
                            noFilter: true,
                            warningChangeModal: {
                                warningText: getLiteral(
                                    quoteProductByEnvironment
                                        ? 'warning_account_change_environment_orders'
                                        : 'warning_rate_change_salesorders',
                                ),
                                canShow: (data) => {
                                    return (
                                        (quoteProductByEnvironment && data?.products?.length > 0) ||
                                        (state?.serverList?.ratesValueList?.data?.length &&
                                            data?.products?.length > 0 &&
                                            !salesOrderManualPrices)
                                    );
                                },
                            },
                        },
                        column: 1,
                        hiddenForBulk: true,
                    },
                    {
                        ...defaultInputSchema,
                        id: 'salesOrderType',
                        serverId: 'salesOrderType',
                        fieldConfiguration: 'salesOrderType',
                        preserveInPayload: true,
                        type: 'singleValueList',
                        label: getLiteral('label_salesorder_type'),
                        hint: getLiteral('label_selectone'),
                        inputAttrs: {
                            list: 'tblTiposSalesOrders',
                            actions: getCustomizeCrudAction(
                                SALESORDERS,
                                '/settings/values-list',
                                `?entity=${SALESORDERS.trueName}&list=tblTiposSalesOrders`,
                            ),
                        },
                        column: 1,
                    },
                    {
                        ...defaultInputSchema,
                        id: 'expectedCloseDate',
                        serverId: 'expectedCloseDate',
                        fieldConfiguration: 'expectedCloseDate',
                        type: 'date',
                        label: getLiteral('label_valid_until'),
                        column: 2,
                        hiddenForBulk: true,
                    },
                    {
                        ...defaultInputSchema,
                        id: 'idRate',
                        serverId: 'idRate',
                        fieldConfiguration: 'idTarifa',
                        preserveInPayload: true,
                        type: 'singleValueList',
                        label: getLiteral('label_rate'),
                        fakeError: this.getRateCaducityError,
                        hasDefaultUserValues: true,
                        inputAttrs: {
                            list: 'ratesValueList',
                            hideIfEmptyList: true,
                            emptyFilter: emptyFilterRate,
                            beforeRenderValueList: this.filterTblRates,
                            withNoOptionValue: true, // show value even if not in the list
                            warningChangeModal: {
                                warningText: getLiteral('warning_rate_change_salesorders'),
                                canShow: (data) =>
                                    state?.serverList?.ratesValueList?.data?.length &&
                                    data?.products?.length > 0 &&
                                    !salesOrderManualPrices,
                            },
                        },
                        column: 1,
                        hiddenForBulk: true,
                    },
                    {
                        ...defaultInputSchema,
                        id: 'idCurrency',
                        preserveInPayload: true,
                        type: 'singleValueList',
                        label: getLiteral('common_text_currency'),
                        column: 1,
                        inputAttrs: {
                            list: 'tblCurrency',
                            isClearable: false,
                        },
                        hiddenForBulk: true,
                    },
                    {
                        ...defaultInputSchema,
                        id: 'idAddress',
                        serverId: 'idAddress',
                        fieldConfiguration: 'idCompanyAddress',
                        preserveInPayload: true,
                        type: 'shippingAddress',
                        isCustom: true,
                        label: getLiteral('label_shipping_address_salesorders'),
                        column: 1,
                        hiddenForBulk: true,
                    },
                    {
                        ...defaultInputSchema,
                        id: 'idStateSalesOrder',
                        serverId: 'idStateSalesOrder',
                        fieldConfiguration: 'idEstadoSalesOrder',
                        preserveInPayload: true,
                        type: 'singleValueList',
                        label: getLiteral('label_status_salesorders'),
                        hint: getLiteral('label_selectone'),
                        inputAttrs: {
                            list: 'tblEstadosSalesOrders',
                            getExtraClassNameValue: this.getExtraClassNameStateValue,
                            actions: getCustomizeCrudAction(
                                SALESORDERS,
                                '/settings/values-list',
                                `?entity=${SALESORDERS.trueName}&list=tblEstadosSalesOrders`,
                            ),
                        },
                        column: 2,
                    },
                ],
            },
        ];
        return schema;
    };

    afterGetSchema = ({
        schema,
        dependencyMap,
        dynamicMap,
        smartFieldsMap,
        smartFieldsDependencyMap,
    }) =>
        new Promise((resolve) => {
            const enableSalesOrdersTypes =
                Context.store.getState().config.userData.enableSalesOrdersTypes;

            schema.map((currElement, index) => {
                schema[index].fields = currElement.fields.filter((field) => {
                    switch (field.id) {
                        case 'salesOrderType':
                            if (!enableSalesOrdersTypes) return false;
                            else {
                                field.mandatory = true;
                                field.hidden = false;
                            }
                            return true;
                        default:
                            return true;
                    }
                });
            });

            resolve({
                schema,
                dependencyMap,
                dynamicMap,
                smartFieldsMap,
                smartFieldsDependencyMap,
            });
        });

    moreFieldsSchema = (defaultInputSchema, isCreate) =>
        new Promise((resolve) => {
            const state = Context.store.getState();
            const useTaxesSalesOrders = state?.config?.userData?.useTaxesSalesOrders || false;
            const uomEnabled = !!state?.config?.userData?.uomEnabled;
            Context.extraFieldManager
                .getExtraFieldsCrudSchema(SALESORDERSLINES, defaultInputSchema)
                .then((result) => {
                    const { extraSchema } = Context.entityCrudManager.extraFieldsConfiguration(
                        result,
                        isCreate,
                    );

                    const extraFields = extraSchema.reduce((arr, tabs) => {
                        return [...arr, ...tabs.fields];
                    }, []);

                    let standardSchema = [
                        {
                            ...defaultInputSchema,
                            id: 'entityId',
                            serverId: 'entityId',
                            fieldConfiguration: 'entityId',
                        },
                        {
                            ...defaultInputSchema,
                            id: 'idProduct',
                            serverId: 'idProduct',
                            fieldConfiguration: 'idProduct',
                        },
                        {
                            ...defaultInputSchema,
                            id: 'quantity',
                            serverId: 'quantity',
                            fieldConfiguration: 'quantity',
                        },
                        ...(uomEnabled
                            ? [
                                  {
                                      ...defaultInputSchema,
                                      id: 'idRelationUoM',
                                      serverId: 'idRelationUoM',
                                      fieldConfiguration: 'idRelationUoM',
                                      mandatory: false,
                                      type: 'singleValueList',
                                  },
                              ]
                            : []),
                        {
                            ...defaultInputSchema,
                            id: 'price',
                            serverId: 'price',
                            fieldConfiguration: 'price',
                        },
                        {
                            ...defaultInputSchema,
                            id: 'discount1',
                            serverId: 'discount1',
                            fieldConfiguration: 'discount1',
                        },
                        {
                            ...defaultInputSchema,
                            id: 'discount2',
                            serverId: 'discount2',
                            fieldConfiguration: 'discount2',
                        },
                        {
                            ...defaultInputSchema,
                            id: 'discount3',
                            serverId: 'discount3',
                            fieldConfiguration: 'discount3',
                        },
                        {
                            ...defaultInputSchema,
                            id: 'discount4',
                            serverId: 'discount4',
                            fieldConfiguration: 'discount4',
                        },
                        ...(this.salesOrderAmountDiscountsEnabled(state)
                            ? [1, 2, 3, 4].map((i) => {
                                  const fieldId = `discount${i}`;
                                  const tierSchema =
                                      state?.config?.standardFieldsSchemaMap?.[
                                          SALESORDERSLINES.extraFieldName
                                      ]?.[fieldId];
                                  const fromAttrs = tierSchema?.inputAttrs?.decimalType;
                                  const decimalType =
                                      fromAttrs && Object.values(DECIMAL_TYPES).includes(fromAttrs)
                                          ? fromAttrs
                                          : DECIMAL_TYPES.GENERAL;
                                  return {
                                      ...defaultInputSchema,
                                      id: `${fieldId}Value`,
                                      serverId: `${fieldId}Value`,
                                      fieldConfiguration: `${fieldId}Value`,
                                      type: 'decimal',
                                      inputAttrs: {
                                          decimalType,
                                      },
                                  };
                              })
                            : []),
                        {
                            ...defaultInputSchema,
                            id: 'finalPrice',
                            serverId: 'finalPrice',
                            fieldConfiguration: 'finalPrice',
                        },
                        ...(useTaxesSalesOrders
                            ? [
                                  {
                                      ...defaultInputSchema,
                                      id: 'subtotalAmount',
                                      serverId: 'subtotalAmount',
                                      fieldConfiguration: 'subtotalAmount',
                                      type: 'decimal',
                                      readOnly: true,
                                      inputAttrs: {
                                          decimalType: DECIMAL_TYPES.CURRENCY_PRICES,
                                      },
                                  },
                                  {
                                      ...defaultInputSchema,
                                      id: 'taxRateId',
                                      serverId: 'taxRateId',
                                      fieldConfiguration: 'taxRateId',
                                      type: 'singleValueList',
                                      inputAttrs: {
                                          list: 'tblTaxRate',
                                          // Tax context must always be explicit for line recalculation.
                                          isClearable: false,
                                      },
                                  },
                                  {
                                      ...defaultInputSchema,
                                      id: 'totalAmount',
                                      serverId: 'totalAmount',
                                      fieldConfiguration: 'totalAmount',
                                      type: 'decimal',
                                      readOnly: true,
                                      inputAttrs: {
                                          decimalType: DECIMAL_TYPES.CURRENCY_PRICES,
                                      },
                                  },
                              ]
                            : []),
                        {
                            ...defaultInputSchema,
                            id: 'description',
                            serverId: 'description',
                            fieldConfiguration: 'description',
                        },
                    ];

                    if (isSageImplementation(Context.store.getState()?.config?.productModeCode)) {
                        standardSchema = standardSchema.filter(
                            (f) =>
                                ![
                                    'discount2',
                                    'discount3',
                                    'discount4',
                                    'discount2Value',
                                    'discount3Value',
                                    'discount4Value',
                                ].includes(f.id),
                        );
                    }

                    standardSchema =
                        Context.entityCrudManager.standardFieldsConfiguration(
                            this,
                            [{ fields: standardSchema }],
                            SALESORDERSLINES,
                            isCreate,
                        )?.[0]?.fields || [];

                    // Calculated line amounts are always read-only in Sales Orders
                    // regardless of standard field configuration.
                    const calculatedLineFields = ['finalprice', 'subtotalamount', 'totalamount'];
                    standardSchema = standardSchema.map((field) => {
                        const fieldIds = [
                            `${field?.id || ''}`.toLowerCase(),
                            `${field?.serverId || ''}`.toLowerCase(),
                            `${field?.fieldConfiguration || ''}`.toLowerCase(),
                        ];
                        if (fieldIds.some((id) => calculatedLineFields.includes(id))) {
                            return { ...field, readOnly: true };
                        }
                        return field;
                    });

                    const schema = [
                        {
                            title: getLiteral('label_products'),
                            show: true,
                            fields: [
                                {
                                    ...defaultInputSchema,
                                    id: 'products',
                                    serverId: 'products',
                                    fieldConfiguration: 'products',
                                    type: 'customField',
                                    isCustom: true,
                                    schema: [...standardSchema, ...extraFields],
                                    hiddenForBulk: true,
                                },
                            ],
                        },
                        {
                            title: getLiteral('label_discounts'),
                            show: true,
                            beforeRenderCrudSection: this.beforeRenderSalesOrderDiscountsSection,
                            fields: [
                                {
                                    ...defaultInputSchema,
                                    id: 'salesOrderDiscounts',
                                    serverId: 'salesOrderDiscounts',
                                    fieldConfiguration: 'salesOrderDiscounts',
                                    type: 'customField',
                                    isCustom: true,
                                    hiddenForBulk: true,
                                },
                            ],
                        },
                        {
                            title: getLiteral('label_additional_info'),
                            show: true,
                            fields: [
                                {
                                    ...defaultInputSchema,
                                    id: 'idResponsible',
                                    serverId: 'idResponsible',
                                    fieldConfiguration: 'idComercial',
                                    preserveInPayload: true,
                                    type: 'fuzzySearchSingle',
                                    label: getLiteral('label_owner'),
                                    hint: getLiteral('action_searchowner'),
                                    hasDefaultUserValues: true,
                                    inputAttrs: {
                                        ...FuzzyMap.users,
                                    },
                                },
                                {
                                    ...defaultInputSchema,
                                    id: 'idContact',
                                    serverId: 'idContact',
                                    fieldConfiguration: 'idContacto',
                                    type: 'fuzzySearchSingle',
                                    label: getLiteral('label_contact'),
                                    hint: getLiteral('action_search_contact'),
                                    inputAttrs: {
                                        ...FuzzyMap.contactos,
                                        parentField: 'idEmpresa',
                                        parentValueType: 'fuzzySearch',
                                        readOnlyIfNoParent: true,
                                        parentFieldForContext: 'idCompany',
                                    },
                                    hiddenForBulk: true,
                                },
                                {
                                    ...defaultInputSchema,
                                    id: 'idOpportunity',
                                    serverId: 'idOpportunity',
                                    fieldConfiguration: 'idExpediente',
                                    type: 'fuzzySearchSingle',
                                    label: getLiteral('title_opportunity'),
                                    hint: getLiteral('action_searchopportunity'),
                                    inputAttrs: {
                                        groupFunction: getGroupByParentValue,
                                        ...FuzzyMap.expedientes,
                                        parentFieldNoRemove: true,
                                        parentField: 'idEmpresa',
                                        parentFieldForBackend: 'idinstalador',
                                        parentFieldForContext: 'idCompany',
                                        parentFieldForContext2: 'idContact',
                                        mainGroupLabel: getLiteral('label_related_opportunities'),
                                        otherGroupLabel: getLiteral('label_other_opportunities'),
                                    },
                                },
                            ],
                        },
                        {
                            title: getLiteral('label_comment_salesorders'),
                            show: true,
                            fields: [
                                {
                                    ...defaultInputSchema,
                                    id: 'comments',
                                    serverId: 'comments',
                                    fieldConfiguration: 'comments',
                                    type: 'textarea',
                                    label: getLiteral('label_comment_salesorders'),
                                    description: getLiteral('label_comments_salesorders_info'),
                                    inputAttrs: {
                                        isHighlighted: false,
                                        rows: 2,
                                        rowsMax: 10,
                                        withoutResizer: true,
                                    },
                                    hiddenForBulk: true,
                                },
                                {
                                    ...defaultInputSchema,
                                    id: 'internalComments',
                                    serverId: 'internalComments',
                                    fieldConfiguration: 'internalComments',
                                    type: 'textarea',
                                    label: getLiteral('label_internalcomment'),
                                    description: getLiteral('helptext_sales_team_comments'),
                                    inputAttrs: {
                                        isHighlighted: false,
                                        rows: 2,
                                        rowsMax: 10,
                                        withoutResizer: true,
                                    },
                                    hiddenForBulk: true,
                                },
                            ],
                        },
                    ];

                    resolve(schema);
                })
                .catch((error) => {
                    console.error('error getting extrafields schema for salesorderslines:', error);
                    resolve([]);
                });
        });

    beforeCrud() {
        return new Promise((resolve) => {
            Promise.all([this.getTblRates()])
                .then(([rates]) => {
                    resolve({ extraData: { rates } });
                })
                .catch((error) => {
                    console.error(error);
                });
        });
    }

    beforeRenderValueList(options) {
        const config = Context.store.getState().config;
        const { currencySymbol, locale } = config.userData;

        const finalOptions = options.map((option) => {
            const { label, value, ExtraInfo: description, ExtraInfoId: price } = option;
            const subLabel = [];

            if (description) subLabel.push(description);
            if (price) {
                subLabel.push(
                    getCurrency({
                        number: price,
                        currencySymbol,
                        precision: 2,
                        locale,
                    }),
                );
            }

            return {
                ...option,
                label,
                value,
                subLabel,
            };
        });

        return finalOptions;
    }

    getSpecificEntityDefaultValues(data, id) {
        if (!data) return;
        const config = Context.store.getState().config;

        if (!id) {
            // creation only
            if (!data.idResponsible || isEmpty(data.idResponsible)) {
                data.idResponsible = {
                    label: `${config.userData.nombre} ${config.userData.apellidos}`,
                    value: config.userData.idUsuario,
                };
            }
            const userRate = parseInt(config.userData.idTarifa, 10) || -1;
            const userCurrency = config.userData.currencyISO || '';
            const rate =
                data?.extraData?.rates &&
                userRate !== -1 &&
                data.extraData.rates.find((r) => Number(r.value) === userRate);

            if (
                !rate &&
                data.extraData.rates.find((r) => Number(r.value) === Number(data.idRate))
            ) {
                rate = data.extraData.rates.find((r) => Number(r.value) === Number(data.idRate));
            }

            if (rate) data.idRate = rate.value;
            if (rate?.currency || userCurrency) {
                data.idCurrency = rate?.idCurrency;
                data.currency = rate?.currency;
                data.currencySymbol = rate?.currencySymbol;
            }

            if (!data.idCurrency && config.userData.currencyId) {
                data.currency = config.userData.currencyISO || '';
                data.currencySymbol = config.userData.currencySymbol;
                data.idCurrency = parseInt(config.userData.currencyId, 10);
            }

            // Ask backend why they send different key on defaultValues and standardFieldsSchema
            // for idStateSalesOrder field default value configuration
            if (!data.idStateSalesOrder) {
                let defaultValueFields = config.defaultvaluefields.entity.find(
                    (e) => e.name === SALESORDERS.customField,
                );
                if (defaultValueFields) {
                    defaultValueFields = defaultValueFields.defaultvaluefield;
                    defaultValueFields = defaultValueFields.reduce((obj, d) => {
                        obj[d.field.toLowerCase()] = d;
                        return obj;
                    }, {});
                    if (defaultValueFields.idstatesalesorder) {
                        data.idStateSalesOrder = defaultValueFields.idstatesalesorder.value;
                    }
                }
            }
        }

        return data;
    }

    resetOnEnvironmentChange({ fields, data }) {
        const config = Context.store.getState().config;

        fields.idResponsible = {
            label: `${config.userData.nombre} ${config.userData.apellidos}`,
            value: config.userData.idUsuario,
        };
        const userRate = parseInt(config.userData.idTarifa, 10) || -1;
        const userCurrency = config.userData.currencyISO || '';
        const rate =
            data?.extraData?.rates &&
            userRate !== -1 &&
            data.extraData.rates.find((r) => Number(r.value) === userRate);

        if (rate) fields.idRate = rate.value;
        if (userCurrency) {
            fields.idCurrency = rate?.idCurrency;
            fields.currency = rate?.currency;
            fields.currencySymbol = rate?.currencySymbol;
        }

        if (config.userData.currencyId) {
            fields.currency = config.userData.currencyISO || '';
            fields.currencySymbol = config.userData.currencySymbol;
            fields.idCurrency = parseInt(config.userData.currencyId, 10);
        }

        let defaultValueFields = config.defaultvaluefields.entity.find(
            (e) => e.name === SALESORDERS.customField,
        );
        if (defaultValueFields) {
            defaultValueFields = defaultValueFields.defaultvaluefield;
            defaultValueFields = defaultValueFields.reduce((obj, d) => {
                obj[d.field.toLowerCase()] = d;
                return obj;
            }, {});
            if (defaultValueFields.idstatesalesorder) {
                fields.idStateSalesOrder = defaultValueFields.idstatesalesorder.value;
            }
        }
    }

    getTblRates() {
        return new Promise((resolve) => {
            Context.serverListManager
                .getList('ratesValueList')
                .then((result) => {
                    resolve(this.filterTblRates(result));
                })
                .catch((error) => {
                    console.error(error);
                });
        });
    }

    filterTblRates(options) {
        if (!options) return options;
        return options.filter((option) => {
            const { startDate, endDate } = option;
            if (startDate === null && endDate === null) return true;
            if (endDate === '01/01/1900 00:00:00') return true;
            const startDateNum = getDifferentDay(startDate, 'DD/MM/YYYY HH:mm:ss');
            const endDateNum = getDifferentDay(endDate, 'DD/MM/YYYY HH:mm:ss');
            return startDateNum <= 0 && endDateNum >= 0;
        });
    }

    hasCopilotProductLines(products) {
        return (
            Array.isArray(products) &&
            products.some((line) => line?.idproduct != null && line?.id == null)
        );
    }

    getProductLineDefaultValues(mappedSchema) {
        return (
            mappedSchema?.products?.schema?.reduce(
                (obj, field) =>
                    Context.entityCrudManager.getDbDefaultValuesField(obj, {
                        ...field,
                        id: field.id.toLowerCase(),
                    }),
                {},
            ) ?? {}
        );
    }

    buildCrudLineFromCopilotProduct(copilotLine, { dataCrud, defaultValueFields, state }) {
        const lineIdEnvironment = dataCrud.idBranch?.value || dataCrud.idBranch;
        const lineIdCurrency = dataCrud.idCurrency?.value || dataCrud.idCurrency;
        const rawQuantity = copilotLine.quantity ?? 1;
        const productPriceRaw = copilotLine.price ?? defaultValueFields?.price ?? 0;
        const productPrice = roundDecimalByConfig({
            value: productPriceRaw,
            type: DECIMAL_TYPES.CURRENCY_PRICES,
            idEnvironment: lineIdEnvironment,
            idCurrency: lineIdCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId: 'price',
        });

        const modalProduct = {
            discount1: copilotLine.discount1,
            discount2: copilotLine.discount2,
            discount3: copilotLine.discount3,
            discount4: copilotLine.discount4,
            finalprice: productPriceRaw,
            id: copilotLine.idproduct,
            idThumbnail: copilotLine.idThumbnail,
            name: copilotLine.model,
        };

        let crudProduct = {
            ...defaultValueFields,
            ...copilotLine,
            description: copilotLine.description,
            discount1: copilotLine.discount1,
            discount2: copilotLine.discount2,
            discount3: copilotLine.discount3,
            discount4: copilotLine.discount4,
            id: `${copilotLine.idproduct}/${randomId()}`,
            idThumbnail: copilotLine.idThumbnail,
            idproduct: copilotLine.idproduct,
            isNew: true,
            model: copilotLine.model,
            price: productPrice,
            productPrice,
            quantity: roundDecimalByConfig({
                value: rawQuantity,
                type: DECIMAL_TYPES.QUANTITY,
                idEnvironment: lineIdEnvironment,
                idCurrency: lineIdCurrency,
                entityName: 'SALESORDERSLINES',
                fieldId: 'quantity',
            }),
        };

        const initialTax = this.resolveInitialLineTaxForNewLine({
            defaultValueFields,
            lineIdEnvironment,
            modalProduct,
            salesOrderData: dataCrud,
            state,
        });
        crudProduct.taxRateId = initialTax.taxRateId;
        crudProduct.taxRate = initialTax.taxRate;
        delete crudProduct.taxrateid;

        if (defaultValueFields) {
            for (let i = 1; i <= 4; i++) {
                if (
                    !crudProduct[`discount${i}`] &&
                    crudProduct[`discount${i}`] !== 0 &&
                    defaultValueFields[`discount${i}`]
                ) {
                    crudProduct[`discount${i}`] = parseInt(defaultValueFields[`discount${i}`], 10);
                }
            }
        }

        for (let i = 1; i <= 4; i++) {
            const fieldId = `discount${i}`;
            const discountValue = crudProduct[fieldId];
            if (discountValue === undefined || discountValue === null || discountValue === '') {
                continue;
            }

            crudProduct[fieldId] = roundDecimalByConfig({
                entityName: 'SALESORDERSLINES',
                fieldId,
                idCurrency: lineIdCurrency,
                idEnvironment: lineIdEnvironment,
                type: DECIMAL_TYPES.GENERAL,
                value: discountValue,
            });
        }

        return this.recalculateProductLine(crudProduct, dataCrud, state);
    }

    buildCrudLinesFromCopilotProducts(copilotLines, dataCrud, mappedSchema, state) {
        const defaultValueFields = this.getProductLineDefaultValues(mappedSchema);

        return copilotLines
            .filter((line) => line?.idproduct != null)
            .map((line) => {
                if (line.id != null) {
                    return line;
                }

                return this.buildCrudLineFromCopilotProduct(line, {
                    dataCrud,
                    defaultValueFields,
                    state,
                });
            });
    }

    changeFields(fields) {
        const state = Context.store.getState();

        const quoteProductByEnvironment = state?.config?.permission?.quoteProductByEnvironment;
        const multiEnvironment = state?.config?.permission?.multiEnvironment;
        const activeCrud = getActiveCrud(state);
        const dataCrud = activeCrud?.data;
        const mappedSchema = activeCrud?.mappedSchema;
        const config = state.config;
        let newFields = { ...fields };

        if (
            newFields.hasOwnProperty('products') &&
            this.hasCopilotProductLines(newFields.products)
        ) {
            newFields.shippingCostRaw = null;
            const enrichedProducts = this.buildCrudLinesFromCopilotProducts(
                newFields.products,
                dataCrud,
                mappedSchema,
                state,
            );
            const mergedForLines = { ...dataCrud, ...newFields };
            newFields.products = enrichedProducts;
            Object.assign(
                newFields,
                this.getCalculatedAmountFields(mergedForLines, enrichedProducts),
            );
        }

        if (newFields.hasOwnProperty('idRate')) {
            if (!newFields.idRate) {
                // default value for currency
                newFields.currency = config.userData.currencyISO || '';
                newFields.currencySymbol = config.userData.currencySymbol || '';
                newFields.idCurrency = parseInt(config.userData.currencyId, 10) || '';
            } else {
                const newRate = dataCrud?.extraData?.rates?.find(
                    (rate) =>
                        Number(rate.value) === Number(newFields.idRate?.value || newFields.idRate),
                );
                newFields.currency = newRate?.currency || config.userData.currencyISO || '';
                newFields.currencySymbol =
                    newRate?.currencySymbol || config.userData.currencySymbol || '';
                newFields.idCurrency =
                    newRate?.idCurrency || parseInt(config.userData.currencyId, 10) || '';
            }
            const productsSource = newFields.hasOwnProperty('products')
                ? newFields.products
                : dataCrud?.products;
            newFields.products =
                productsSource?.map((current) => ({
                    ...current,
                    currencySymbol: newFields.currencySymbol,
                })) || [];
        }

        if (newFields.hasOwnProperty('idCompany')) {
            const extraFieldId = mappedSchema.idCompany.inputAttrs.extraValueField;
            // Persist IVA-exempt flag on order data: idCompany in CRUD is often reduced to { label, value }
            // and ExtraInfo5 from companyrates is lost, so new lines cannot infer exempt later.
            newFields.salesOrderCompanyTaxExempt = newFields.idCompany
                ? getBackendBoolean(newFields.idCompany.ExtraInfo5)
                : false;

            if (!newFields.idCompany) {
                // Address changes
                newFields.idAddress = '';
                newFields.address = '';
                newFields[extraFieldId] = null;

                // Clear company warning
                newFields.companyWarning = '';

                // Rate changes
                newFields.idRate = config.userData.idTarifa;
                newFields.currency = config.userData.currencyISO || '';
                newFields.currencySymbol = config.userData.currencySymbol || '';
                newFields.idCurrency = parseInt(config.userData.currencyId, 10) || '';

                if (quoteProductByEnvironment && !multiEnvironment) {
                    newFields.idBranch = null;
                }
            } else {
                // Address changes
                newFields[extraFieldId] = newFields.idCompany.ExtraInfo4;
                if (newFields.idCompany.ExtraInfo2) {
                    newFields.idAddress = -1;
                    newFields.address = newFields.idCompany.ExtraInfo2?.trim();
                    newFields.withCompanyAddress = true;
                } else {
                    newFields.idAddress = '';
                }

                newFields.companyWarning = newFields.idCompany.ExtraInfo4 || '';

                // Rate changes
                if (newFields.idCompany.ExtraInfo) {
                    newFields.idRate = {
                        label: newFields.idCompany.ExtraInfo,
                        value: newFields.idCompany.ExtraInfoId,
                    };

                    const newRate = dataCrud?.extraData?.rates?.find(
                        (rate) => Number(rate.value) === Number(newFields.idRate.value),
                    );

                    newFields.currency = newRate?.currency || config.userData.currencyISO || '';
                    newFields.currencySymbol =
                        newRate?.currencySymbol || config.userData.currencySymbol || '';
                    newFields.idCurrency =
                        newRate?.idCurrency || parseInt(config.userData.currencyId, 10) || '';
                }

                if (quoteProductByEnvironment) {
                    newFields.idBranch = newFields.idCompany.ExtraInfoId7 ?? dataCrud?.idBranch;
                }
            }

            if (quoteProductByEnvironment && !newFields.hasOwnProperty('products')) {
                newFields.products = [];
                newFields.shippingCostRaw = null;
                Object.assign(
                    newFields,
                    this.getCalculatedAmountFields(
                        { ...dataCrud, ...newFields, shippingCostRaw: null },
                        [],
                    ),
                );
            } else {
                const mergedForLines = { ...dataCrud, ...newFields, shippingCostRaw: null };
                const companyIsExempt = this.isSalesOrderCompanyTaxExempt(mergedForLines, state);
                const productsSource = newFields.hasOwnProperty('products')
                    ? newFields.products
                    : dataCrud?.products;
                newFields.products =
                    productsSource?.map((current) => {
                        const line = {
                            ...current,
                            currencySymbol:
                                newFields.currencySymbol ?? mergedForLines.currencySymbol,
                        };
                        if (companyIsExempt) {
                            line.salesOrderLineTaxManual = false;
                        }
                        return this.recalculateProductLine(line, mergedForLines, state);
                    }) || [];
                if (newFields.products.length) {
                    Object.assign(
                        newFields,
                        this.getCalculatedAmountFields(mergedForLines, newFields.products),
                    );
                } else if (quoteProductByEnvironment) {
                    newFields.shippingCostRaw = null;
                    Object.assign(
                        newFields,
                        this.getCalculatedAmountFields(mergedForLines, newFields.products),
                    );
                }
            }
        }

        if (
            newFields.hasOwnProperty('idAddress') &&
            !!newFields.idAddress &&
            typeof newFields.idAddress === 'object'
        ) {
            newFields.address = newFields.idAddress.address;
            newFields.address2 = newFields.idAddress.address2;
            newFields.city = newFields.idAddress.city;
            newFields.country = newFields.idAddress.country;
            newFields.idCountry = newFields.idAddress.idCountry;
            newFields.postCode = newFields.idAddress.postCode;
            newFields.province = newFields.idAddress.province;
            newFields.idAddress = newFields.idAddress.idAddress;
            newFields.withCompanyAddress = false;
        }

        const headerDiscountPercentTouched =
            newFields.hasOwnProperty('discount1') ||
            newFields.hasOwnProperty('discount2') ||
            newFields.hasOwnProperty('discount3') ||
            newFields.hasOwnProperty('discount4');
        const headerDiscountValueTouched =
            newFields.hasOwnProperty('discount1Value') ||
            newFields.hasOwnProperty('discount2Value') ||
            newFields.hasOwnProperty('discount3Value') ||
            newFields.hasOwnProperty('discount4Value');

        if (headerDiscountPercentTouched || headerDiscountValueTouched) {
            // Clear Sage shipping cost on header discount change
            // so calculateShippingCost recalculates from lines with the new discounts.
            newFields.shippingCostRaw = null;

            if (this.salesOrderAmountDiscountsEnabled(state)) {
                if (headerDiscountValueTouched) {
                    Object.assign(
                        newFields,
                        this.syncSalesOrderHeaderDiscountsAndTotals(
                            { ...newFields, shippingCostRaw: null },
                            { ...dataCrud, shippingCostRaw: null },
                            state,
                        ),
                    );
                } else if (headerDiscountPercentTouched) {
                    let discount1 = dataCrud.discount1 || null;
                    let discount2 = dataCrud.discount2 || null;
                    let discount3 = dataCrud.discount3 || null;
                    let discount4 = dataCrud.discount4 || null;
                    if (newFields.hasOwnProperty('discount1')) discount1 = newFields.discount1;
                    if (newFields.hasOwnProperty('discount2')) discount2 = newFields.discount2;
                    if (newFields.hasOwnProperty('discount3')) discount3 = newFields.discount3;
                    if (newFields.hasOwnProperty('discount4')) discount4 = newFields.discount4;

                    const mergedForTotals = {
                        ...dataCrud,
                        shippingCostRaw: null,
                        discount1,
                        discount2,
                        discount3,
                        discount4,
                    };
                    this.applyHeaderDiscountPercentClamp(mergedForTotals);
                    // Write clamped percents back so the CRUD form inputs update.
                    for (let i = 1; i <= 4; i += 1) {
                        const key = `discount${i}`;
                        if (newFields.hasOwnProperty(key)) {
                            newFields[key] = mergedForTotals[key];
                        }
                    }

                    const percentTiersToRefresh = [1, 2, 3, 4].filter((i) =>
                        newFields.hasOwnProperty(`discount${i}`),
                    );
                    Object.assign(
                        newFields,
                        this.getCalculatedAmountFields(mergedForTotals, dataCrud.products),
                        this.buildHeaderDiscountValueFieldsFromPercents(mergedForTotals, state, {
                            percentTiersToRefresh,
                        }),
                    );
                }
            } else if (headerDiscountPercentTouched) {
                let discount1 = dataCrud.discount1 || null;
                let discount2 = dataCrud.discount2 || null;
                let discount3 = dataCrud.discount3 || null;
                let discount4 = dataCrud.discount4 || null;
                if (newFields.hasOwnProperty('discount1')) discount1 = newFields.discount1;
                if (newFields.hasOwnProperty('discount2')) discount2 = newFields.discount2;
                if (newFields.hasOwnProperty('discount3')) discount3 = newFields.discount3;
                if (newFields.hasOwnProperty('discount4')) discount4 = newFields.discount4;

                Object.assign(
                    newFields,
                    this.getCalculatedAmountFields(
                        {
                            ...dataCrud,
                            shippingCostRaw: null,
                            discount1,
                            discount2,
                            discount3,
                            discount4,
                        },
                        dataCrud.products,
                    ),
                );
            }
        }

        if (newFields.hasOwnProperty('product')) {
            let defaultValueFields = mappedSchema?.products?.schema?.reduce(
                (obj, field) =>
                    Context.entityCrudManager.getDbDefaultValuesField(obj, {
                        ...field,
                        id: field.id.toLowerCase(),
                    }),
                {},
            );

            const currentProducts = dataCrud.products || [];
            let crudProducts = [];
            const changeInfo = newFields.product;

            if (changeInfo.rowIndex || typeof changeInfo.rowIndex === 'number') {
                const row = currentProducts[changeInfo.rowIndex];
                const lineSchema = mappedSchema?.products?.schema;
                const lineDataKeyByField = {};
                lineSchema?.forEach((sf) => {
                    const configKey = sf.fieldConfiguration || sf.id;
                    const dataKey = sf.isExtra ? sf.id.toLowerCase() : sf.id;
                    lineDataKeyByField[configKey] = dataKey;
                    lineDataKeyByField[configKey.toLowerCase()] = dataKey;
                    lineDataKeyByField[sf.id] = dataKey;
                    lineDataKeyByField[sf.id.toLowerCase()] = dataKey;
                });
                const resolveLineDataKey = (fieldName) =>
                    lineDataKeyByField[fieldName] ??
                    lineDataKeyByField[fieldName?.toLowerCase?.()] ??
                    fieldName?.toLowerCase?.() ??
                    fieldName;

                // Clear Sage shipping cost on any line edit
                // so calculateShippingCost recalculates from lines.
                newFields.shippingCostRaw = null;
                if (changeInfo.field === 'taxRateId') {
                    row.salesOrderLineTaxManual = true;
                }
                const lineField = lineSchema?.find((sf) => {
                    const fieldKeys = [
                        sf.id,
                        sf.id?.toLowerCase?.(),
                        sf.fieldConfiguration,
                        sf.fieldConfiguration?.toLowerCase?.(),
                    ].filter(Boolean);
                    const changeKey = changeInfo.field?.toLowerCase?.() || changeInfo.field;
                    return fieldKeys.some((key) => key?.toLowerCase?.() === changeKey);
                });
                const nextValue =
                    lineField?.type === 'date' || lineField?.type === 'dateTime'
                        ? normalizeLineDateValue(changeInfo.value)
                        : changeInfo.value;
                row[resolveLineDataKey(changeInfo.field)] = nextValue;

                if (changeInfo.field === 'idRelationUoM') {
                    const rawRelationFieldId = changeInfo.value?.value ?? changeInfo.value;
                    const relationFieldId = nullIfBackendEmptyId(rawRelationFieldId);
                    const relation = findProductUnitRelationByStoredUomField(
                        row.productUnitRelations || [],
                        relationFieldId,
                    );
                    row.idRelationUoM = relationFieldId;
                    row.unitOfMeasureRelation = relation || null;
                    if (relation?.unitOfMeasure) {
                        row.unitOfMeasure = relation.unitOfMeasure;
                    } else if (relationFieldId == null) {
                        row.unitOfMeasure = null;
                    }

                    // UoM relation change resets price to the original productPrice,
                    // and applies the relation's specialPrice when present so totals
                    // can use a plain price × quantity (factor bypassed).
                    const lineIdEnvironment = dataCrud.idBranch?.value || dataCrud.idBranch;
                    const lineIdCurrency = dataCrud.idCurrency?.value || dataCrud.idCurrency;
                    const productPrice = row.productPrice ?? row.price ?? 0;
                    let nextPrice = productPrice;
                    if (
                        relation &&
                        getBackendBoolean(relation.useSpecialPrice) &&
                        relation.specialPrice != null
                    ) {
                        nextPrice = relation.specialPrice;
                    }
                    row.price = roundDecimalByConfig({
                        value: nextPrice,
                        type: DECIMAL_TYPES.CURRENCY_PRICES,
                        idEnvironment: lineIdEnvironment,
                        idCurrency: lineIdCurrency,
                        entityName: 'SALESORDERSLINES',
                        fieldId: 'price',
                    });

                    // Reset quantity to the new relation's minStep (or 1 if absent) so
                    // minStep validation passes after switching units.
                    const nextMinStep = this.parseDecimalValue(relation?.minStep);
                    row.quantity = roundDecimalByConfig({
                        value: nextMinStep > 0 ? nextMinStep : 1,
                        type: DECIMAL_TYPES.QUANTITY,
                        idEnvironment: lineIdEnvironment,
                        idCurrency: lineIdCurrency,
                        entityName: 'SALESORDERSLINES',
                        fieldId: 'quantity',
                    });
                }

                if (
                    this.salesOrderAmountDiscountsEnabled(state) &&
                    /^discount[1-4]Value$/.test(changeInfo.field)
                ) {
                    this.syncLineDiscountsFromValueEdit(row, dataCrud, state, changeInfo.field);
                }

                let updatedRow = row;
                if (lineSchema?.length) {
                    const { rows } = applySalesOrderLinesSmartFields({
                        crudSchema: lineSchema,
                        rows: [row],
                        parentConfigMap: activeCrud?.configMap,
                        parentData: { ...dataCrud, ...newFields },
                        isNewEntity: row.isNew ?? !dataCrud?.id,
                    });
                    updatedRow = rows[0];
                }

                currentProducts[changeInfo.rowIndex] = this.recalculateProductLine(
                    updatedRow,
                    dataCrud,
                    state,
                );
            } else {
                const modalProducts = Array.isArray(newFields.product)
                    ? newFields.product
                    : [newFields.product];

                const lineIdEnvironment = dataCrud.idBranch?.value || dataCrud.idBranch;
                const lineIdCurrency = dataCrud.idCurrency?.value || dataCrud.idCurrency;

                crudProducts = modalProducts
                    .filter((newFieldProduct) => newFieldProduct.product)
                    .map((newFieldProduct) => {
                        const modalProduct = newFieldProduct.product;
                        const fieldQuantity = newFieldProduct.quantity;
                        const recommendedQuantity = modalProduct.hasOwnProperty(
                            'recommendedQuantity',
                        )
                            ? modalProduct.recommendedQuantity
                            : -1;

                        const rawQuantity =
                            fieldQuantity || fieldQuantity === 0
                                ? fieldQuantity
                                : recommendedQuantity;
                        const productPriceRaw =
                            modalProduct.finalprice ?? defaultValueFields.price ?? 0;
                        const productPrice = roundDecimalByConfig({
                            value: productPriceRaw,
                            type: DECIMAL_TYPES.CURRENCY_PRICES,
                            idEnvironment: lineIdEnvironment,
                            idCurrency: lineIdCurrency,
                            entityName: 'SALESORDERSLINES',
                            fieldId: 'price',
                        });

                        let crudProduct = {
                            ...defaultValueFields,
                            id: modalProduct.id + `/${randomId()}`,
                            isNew: true,
                            idproduct: modalProduct.id,
                            productTypeCode:
                                modalProduct.productTypeCode ?? modalProduct.ProductTypeCode,
                            model: modalProduct.name,
                            quantity: roundDecimalByConfig({
                                value: rawQuantity,
                                type: DECIMAL_TYPES.QUANTITY,
                                idEnvironment: lineIdEnvironment,
                                idCurrency: lineIdCurrency,
                                entityName: 'SALESORDERSLINES',
                                fieldId: 'quantity',
                            }),
                            // Preserved original product unit price; backend returns it and
                            // we also set it here so UoM changes can reset `price` back to
                            // the original value.
                            productPrice,
                            price: productPrice,
                            discount1: modalProduct.discount1,
                            discount2: modalProduct.discount2,
                            discount3: modalProduct.discount3,
                            discount4: modalProduct.discount4,
                            idThumbnail: modalProduct.idThumbnail,
                            description: modalProduct.description,
                            imageList: modalProduct.imageList,
                        };

                        if (state?.config?.userData?.uomEnabled) {
                            const productUnitRelations = modalProduct.productUnitRelations || [];
                            crudProduct.productUnitRelations = productUnitRelations;
                            // Default line UoM: product’s **base** relation first, then product default UoM.
                            const baseRelation =
                                productUnitRelations.find(
                                    (r) =>
                                        getBackendBoolean(r.isBase) &&
                                        getBackendBoolean(r.isActive),
                                ) || productUnitRelations.find((r) => getBackendBoolean(r.isBase));
                            const initialRelationRowId = nullIfBackendEmptyId(
                                baseRelation?.id ?? baseRelation?.Id ?? null,
                            );
                            const initialCatalogUomId = nullIfBackendEmptyId(
                                baseRelation?.unitOfMeasure?.id ??
                                    modalProduct.unitOfMeasure?.id ??
                                    null,
                            );
                            const initialIdUom = initialRelationRowId ?? initialCatalogUomId;
                            crudProduct.idRelationUoM = initialIdUom;
                            const initialRelation = findProductUnitRelationByStoredUomField(
                                productUnitRelations,
                                initialIdUom,
                            );
                            crudProduct.unitOfMeasureRelation = initialRelation;
                            crudProduct.unitOfMeasure =
                                crudProduct.unitOfMeasureRelation?.unitOfMeasure ??
                                modalProduct.unitOfMeasure ??
                                null;

                            // Special price overrides the product price at the line level so
                            // totals use a standard price × quantity (factor is bypassed).
                            if (
                                initialRelation &&
                                getBackendBoolean(initialRelation.useSpecialPrice) &&
                                initialRelation.specialPrice != null
                            ) {
                                crudProduct.price = roundDecimalByConfig({
                                    value: initialRelation.specialPrice,
                                    type: DECIMAL_TYPES.CURRENCY_PRICES,
                                    idEnvironment: lineIdEnvironment,
                                    idCurrency: lineIdCurrency,
                                    entityName: 'SALESORDERSLINES',
                                    fieldId: 'price',
                                });
                            }
                        }

                        const initialTax = this.resolveInitialLineTaxForNewLine({
                            modalProduct,
                            defaultValueFields,
                            lineIdEnvironment,
                            salesOrderData: dataCrud,
                            state,
                        });
                        crudProduct.taxRateId = initialTax.taxRateId;
                        crudProduct.taxRate = initialTax.taxRate;
                        delete crudProduct.taxrateid;

                        if (defaultValueFields) {
                            // parsing default discounts for products in SalesOrders
                            for (let i = 1; i <= 4; i++) {
                                if (
                                    !crudProduct[`discount${i}`] &&
                                    crudProduct[`discount${i}`] !== 0 &&
                                    defaultValueFields[`discount${i}`]
                                ) {
                                    crudProduct[`discount${i}`] = parseInt(
                                        defaultValueFields[`discount${i}`],
                                        10,
                                    );
                                }
                            }
                        }

                        for (let i = 1; i <= 4; i++) {
                            const fieldId = `discount${i}`;
                            const discountValue = crudProduct[fieldId];
                            if (
                                discountValue === undefined ||
                                discountValue === null ||
                                discountValue === ''
                            ) {
                                continue;
                            }

                            crudProduct[fieldId] = roundDecimalByConfig({
                                value: discountValue,
                                type: DECIMAL_TYPES.GENERAL,
                                idEnvironment: lineIdEnvironment,
                                idCurrency: lineIdCurrency,
                                entityName: 'SALESORDERSLINES',
                                fieldId,
                            });
                        }

                        return this.recalculateProductLine(crudProduct, dataCrud, state);
                    });
            }

            // Clear Sage shipping cost on any line add
            // so calculateShippingCost recalculates from lines.
            newFields.shippingCostRaw = null;

            const newProducts = [...currentProducts, ...crudProducts];
            delete newFields.product;
            Object.assign(
                newFields,
                this.getCalculatedAmountFields({ ...dataCrud, shippingCostRaw: null }, newProducts),
            );
        }

        if (newFields.hasOwnProperty('removeProducts')) {
            const currentProducts = dataCrud.products || [];
            // Clear Sage shipping cost when a transport line is removed
            // so calculateShippingCost recalculates from lines.
            if (
                currentProducts.some(
                    (p) =>
                        newFields.removeProducts.includes(p.id.toString()) &&
                        (p.productTypeCode ?? p.entity?.productTypeCode) === 'transport',
                )
            ) {
                newFields.shippingCostRaw = null;
            }
            // Clear Sage shipping cost on any line removal
            // so calculateShippingCost recalculates from lines.
            newFields.shippingCostRaw = null;
            const finalProducts = currentProducts.reduce((arr, current) => {
                if (newFields.removeProducts.includes(current.id.toString())) return arr;
                arr.push(current);
                return arr;
            }, []);
            Object.assign(
                newFields,
                this.getCalculatedAmountFields(
                    { ...dataCrud, shippingCostRaw: null },
                    finalProducts,
                ),
            );
        }

        if (newFields.hasOwnProperty('orderedProducts')) {
            newFields.products = newFields.orderedProducts;
        }

        if (newFields.hasOwnProperty('idCurrency')) {
            const currencyList = state?.serverList?.tblCurrency?.data;
            const selectedCurrency = currencyList?.find(
                (currency) =>
                    Number(currency.value) ===
                    Number(newFields.idCurrency?.value || newFields.idCurrency),
            );

            if (selectedCurrency) {
                newFields.currency = selectedCurrency.striso4217code;
                newFields.currencySymbol = selectedCurrency.strsymbol;
                newFields.idCurrency = selectedCurrency.value;

                const currentProducts = newFields.products || dataCrud?.products || [];
                newFields.products = currentProducts.map((current) =>
                    this.recalculateProductLine(
                        { ...current, currencySymbol: newFields.currencySymbol },
                        { ...dataCrud, ...newFields },
                        state,
                    ),
                );

                Object.assign(
                    newFields,
                    this.getCalculatedAmountFields(
                        {
                            ...dataCrud,
                            ...newFields,
                            shippingCostRaw: null,
                            discount1: newFields.discount1 ?? dataCrud.discount1,
                            discount2: newFields.discount2 ?? dataCrud.discount2,
                            discount3: newFields.discount3 ?? dataCrud.discount3,
                            discount4: newFields.discount4 ?? dataCrud.discount4,
                        },
                        newFields.products,
                    ),
                );
            }
        }

        if (this.salesOrderAmountDiscountsEnabled(state)) {
            const headerDiscountKeysInPatch =
                newFields.hasOwnProperty('discount1') ||
                newFields.hasOwnProperty('discount2') ||
                newFields.hasOwnProperty('discount3') ||
                newFields.hasOwnProperty('discount4') ||
                newFields.hasOwnProperty('discount1Value') ||
                newFields.hasOwnProperty('discount2Value') ||
                newFields.hasOwnProperty('discount3Value') ||
                newFields.hasOwnProperty('discount4Value');
            const shouldRefreshHeaderDiscountValues =
                !headerDiscountKeysInPatch &&
                (newFields.hasOwnProperty('products') ||
                    newFields.hasOwnProperty('product') ||
                    newFields.hasOwnProperty('removeProducts') ||
                    newFields.hasOwnProperty('orderedProducts') ||
                    newFields.hasOwnProperty('idCurrency') ||
                    newFields.hasOwnProperty('idRate'));
            if (shouldRefreshHeaderDiscountValues) {
                Object.assign(
                    newFields,
                    this.buildHeaderDiscountValueFieldsFromPercents(
                        { ...dataCrud, ...newFields },
                        state,
                    ),
                );
            }
        }

        const lineSchema = mappedSchema?.products?.schema;
        let productsForSmartFields = newFields.products ?? dataCrud?.products;
        if (lineSchema?.length && productsForSmartFields?.length) {
            const changedConfigKeys = Object.keys(newFields)
                .map((id) => mappedSchema?.[id]?.fieldConfiguration || id)
                .filter((configKey) =>
                    lineSchema.some(
                        (f) =>
                            f.smartField?.parentFieldId?.toLowerCase?.() ===
                            configKey.toLowerCase(),
                    ),
                );
            if (changedConfigKeys.length) {
                productsForSmartFields = clearLineSmartFieldsOnHeaderChange(
                    productsForSmartFields,
                    lineSchema,
                    changedConfigKeys,
                );
            }
            newFields.products = this.resolveProductsWithLineSmartFields(
                productsForSmartFields,
                lineSchema,
                { ...dataCrud, ...newFields },
                state,
            );
        }

        return newFields;
    }

    postChangeFields({ fields, data, dispatchChanges, state }) {
        const salesOrderManualPrices = state?.config?.userData?.salesOrderManualPrices;
        const multiEnvironment = state?.config?.permission?.multiEnvironment;
        if (!data.products?.length || salesOrderManualPrices) return Promise.resolve();

        const productIds = data.products.map((product) => product.idproduct || product.id);
        let promise;

        const idBranch = multiEnvironment
            ? fields.idBranch?.value || fields.idBranch || data.idBranch?.value || data.idBranch
            : -1;

        // Changing company and auto-assigned rate
        if (fields.hasOwnProperty('idCompany') && fields.hasOwnProperty('idRate')) {
            promise = this.context.domainManager.getSalesOrderProductsByIds(
                fields.idRate?.value || fields.idRate || -1,
                fields.idCompany?.value || fields.idCompany || -1,
                idBranch,
                productIds,
            );
        }

        // Changing only company without rate
        if (fields.hasOwnProperty('idCompany') && !fields.hasOwnProperty('idRate')) {
            promise = this.context.domainManager.getSalesOrderProductsByIds(
                data.idRate?.value || data.idRate || -1,
                fields.idCompany?.value || fields.idCompany || -1,
                idBranch,
                productIds,
            );
        }

        // Changing only rate
        if (!fields.hasOwnProperty('idCompany') && fields.hasOwnProperty('idRate')) {
            promise = this.context.domainManager.getSalesOrderProductsByIds(
                fields.idRate?.value || fields.idRate || -1,
                data.idCompany?.value || fields.idCompany || -1,
                idBranch,
                productIds,
            );
        }

        if (!promise) return Promise.resolve();
        return promise
            .then((result) => {
                if (!result?.Result?.length) return;
                const state = Context.store.getState();
                const config = state.config;
                let defaultValueFields = [];
                if (config.defaultvaluefields.entity) {
                    defaultValueFields = config.defaultvaluefields.entity.find(
                        (e) => e.name === SALESORDERSLINES.customField,
                    );
                    if (defaultValueFields) {
                        defaultValueFields = defaultValueFields.defaultvaluefield;
                        defaultValueFields = defaultValueFields.reduce((obj, d) => {
                            obj[d.field.toLowerCase()] = d;
                            return obj;
                        }, {});
                    }
                }

                fields.products = data.products
                    .map((product) => {
                        const updatedProduct = result.Result.find(
                            (item) => item.id === product.idproduct,
                        );
                        if (!updatedProduct) return null;

                        product.productPrice = updatedProduct.finalprice;
                        product.price = updatedProduct.finalprice;
                        // Re-apply special price when the current relation has one so the
                        // rate-change refresh keeps aligned with the UoM flow.
                        const currentRelation =
                            product.unitOfMeasureRelation ||
                            findProductUnitRelationByStoredUomField(
                                product.productUnitRelations || [],
                                product.idRelationUoM,
                            ) ||
                            null;
                        if (
                            currentRelation &&
                            getBackendBoolean(currentRelation.useSpecialPrice) &&
                            currentRelation.specialPrice != null
                        ) {
                            product.price = currentRelation.specialPrice;
                        }
                        product.discount1 =
                            updatedProduct.discount1 || defaultValueFields.discount1?.value;
                        product.discount2 =
                            updatedProduct.discount2 || defaultValueFields.discount2?.value;
                        product.discount3 =
                            updatedProduct.discount3 || defaultValueFields.discount3?.value;
                        product.discount4 =
                            updatedProduct.discount4 || defaultValueFields.discount4?.value;
                        product.taxRateId = updatedProduct.defaultTaxRateId ?? product.taxRateId;
                        product.taxRate = updatedProduct.defaultTaxRate ?? product.taxRate;

                        const mergedOrder = { ...data, ...fields, idBranch };
                        if (
                            this.isSalesOrderCompanyTaxExempt(mergedOrder, state) &&
                            !product.salesOrderLineTaxManual
                        ) {
                            const zeroTax = this.getResolvedZeroVatLineTax(state);
                            product.taxRateId = zeroTax.taxRateId;
                            product.taxRate = zeroTax.taxRate;
                        }

                        return this.recalculateProductLine(product, mergedOrder, state);
                    })
                    .filter((product) => product !== null);

                Object.assign(
                    fields,
                    this.getCalculatedAmountFields(
                        {
                            ...data,
                            ...fields,
                            shippingCostRaw: null,
                            idBranch,
                            idCurrency:
                                fields.idCurrency?.value ||
                                fields.idCurrency ||
                                data.idCurrency?.value ||
                                data.idCurrency,
                        },
                        fields.products,
                    ),
                );

                return dispatchChanges(fields);
            })
            .catch(console.error);
    }

    getLineContextData(data) {
        return {
            idEnvironment: data?.idBranch?.value || data?.idBranch,
            idCurrency: data?.idCurrency?.value || data?.idCurrency,
        };
    }

    salesOrderAmountDiscountsEnabled(state = Context.store.getState()) {
        return !!state?.config?.userData?.salesOrderAmountDiscounts;
    }

    getHeaderDiscountSiblingDecimalRoundConfig(tierIndex, state = Context.store.getState()) {
        const fieldId = `discount${tierIndex}`;
        const schema =
            state?.config?.standardFieldsSchemaMap?.[SALESORDERS.extraFieldName]?.[fieldId];
        const fromAttrs = schema?.inputAttrs?.decimalType;
        const type =
            fromAttrs && Object.values(DECIMAL_TYPES).includes(fromAttrs)
                ? fromAttrs
                : DECIMAL_TYPES.GENERAL;
        return { fieldId, type };
    }

    roundHeaderDiscountValueAmount(value, tierIndex, contextData, state) {
        const { fieldId, type } = this.getHeaderDiscountSiblingDecimalRoundConfig(tierIndex, state);
        return roundDecimalByConfig({
            value,
            type,
            idEnvironment: contextData?.idEnvironment,
            idCurrency: contextData?.idCurrency,
            entityName: 'SALESORDERS',
            fieldId,
        });
    }

    getLineDiscountSiblingDecimalRoundConfig(tierIndex, state = Context.store.getState()) {
        const fieldId = `discount${tierIndex}`;
        const schema =
            state?.config?.standardFieldsSchemaMap?.[SALESORDERSLINES.extraFieldName]?.[fieldId];
        const fromAttrs = schema?.inputAttrs?.decimalType;
        const type =
            fromAttrs && Object.values(DECIMAL_TYPES).includes(fromAttrs)
                ? fromAttrs
                : DECIMAL_TYPES.GENERAL;
        return { fieldId, type };
    }

    roundLineDiscountValueAmount(value, tierIndex, contextData, state) {
        const { fieldId, type } = this.getLineDiscountSiblingDecimalRoundConfig(tierIndex, state);
        return roundDecimalByConfig({
            value,
            type,
            idEnvironment: contextData?.idEnvironment,
            idCurrency: contextData?.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId,
        });
    }

    /**
     * For initial line hydration: per tier, only fill missing % or missing importe.
     * If both are already set, leaves them unchanged (no forced sync).
     */
    mergeLineDiscountPairsForFillMissing(product, salesOrderData, state) {
        if (!this.salesOrderAmountDiscountsEnabled(state)) return {};
        const contextData = this.getLineContextData(salesOrderData);
        const quantity = roundDecimalByConfig({
            value: product.quantity,
            type: DECIMAL_TYPES.QUANTITY,
            idEnvironment: contextData.idEnvironment,
            idCurrency: contextData.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId: 'quantity',
        });
        const price = roundDecimalByConfig({
            value: product.price,
            type: DECIMAL_TYPES.CURRENCY_PRICES,
            idEnvironment: contextData.idEnvironment,
            idCurrency: contextData.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId: 'price',
        });
        const B0 =
            this.parseDecimalValue(quantity) *
            this.getLineEffectiveUnitPrice({ ...product, price });
        const patch = {};
        const d = [1, 2, 3, 4].map((i) => this.parseDecimalValue(product[`discount${i}`]));

        for (let tier = 1; tier <= 4; tier += 1) {
            const valKey = `discount${tier}Value`;
            const rawVal = product[valKey];
            const hasValue = !(rawVal === null || rawVal === undefined || rawVal === '');
            const hasPercent = d[tier - 1] !== 0;

            const bases = this.getSequentialDiscountBasesBeforeEachTier(B0, d[0], d[1], d[2], d[3]);
            const baseBefore = bases[tier - 1];

            if (hasPercent && hasValue) {
                continue;
            }
            if (!hasPercent && !hasValue) {
                patch[valKey] = null;
                continue;
            }
            if (hasPercent && !hasValue) {
                patch[valKey] = this.roundLineDiscountValueAmount(
                    baseBefore * (d[tier - 1] / 100),
                    tier,
                    contextData,
                    state,
                );
                continue;
            }
            if (!hasPercent && hasValue && baseBefore > 0) {
                const rawPct = (this.parseDecimalValue(rawVal) / baseBefore) * 100;
                d[tier - 1] = roundDecimalByConfig({
                    value: rawPct,
                    type: DECIMAL_TYPES.GENERAL,
                    idEnvironment: contextData.idEnvironment,
                    idCurrency: contextData.idCurrency,
                    entityName: 'SALESORDERSLINES',
                    fieldId: `discount${tier}`,
                });
                patch[`discount${tier}`] = d[tier - 1];
            }
        }
        return patch;
    }

    /**
     * Clamp each discount percent to [0, 100] sequentially so the running base never goes negative.
     * Mutates `product` in place. Returns true if any value was capped.
     */
    applyLineDiscountPercentClamp(product) {
        let capped = false;
        for (let i = 1; i <= 4; i += 1) {
            const key = `discount${i}`;
            const raw = this.parseDecimalValue(product[key]);
            if (raw < 0) {
                product[key] = 0;
                capped = true;
            } else if (raw > 100) {
                product[key] = 100;
                capped = true;
            }
        }
        if (capped) {
            warningToast({ text: getLiteral('label_discount_amount_cannot_exceed_line_total') });
        }
        return capped;
    }

    buildLineDiscountValueFieldsFromPercents(product, salesOrderData, state) {
        if (!this.salesOrderAmountDiscountsEnabled(state)) return {};
        const contextData = this.getLineContextData(salesOrderData);
        const quantity = roundDecimalByConfig({
            value: product.quantity,
            type: DECIMAL_TYPES.QUANTITY,
            idEnvironment: contextData.idEnvironment,
            idCurrency: contextData.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId: 'quantity',
        });
        const price = roundDecimalByConfig({
            value: product.price,
            type: DECIMAL_TYPES.CURRENCY_PRICES,
            idEnvironment: contextData.idEnvironment,
            idCurrency: contextData.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId: 'price',
        });
        const B0 =
            this.parseDecimalValue(quantity) *
            this.getLineEffectiveUnitPrice({ ...product, price });
        this.applyLineDiscountPercentClamp(product);
        const d1 = product.discount1;
        const d2 = product.discount2;
        const d3 = product.discount3;
        const d4 = product.discount4;
        const bases = this.getSequentialDiscountBasesBeforeEachTier(B0, d1, d2, d3, d4);
        const valueFields = {};
        for (let i = 1; i <= 4; i += 1) {
            const di = this.parseDecimalValue(product[`discount${i}`]);
            if (!di) {
                valueFields[`discount${i}Value`] = null;
            } else {
                const rawAmount = bases[i - 1] * (di / 100);
                valueFields[`discount${i}Value`] = this.roundLineDiscountValueAmount(
                    rawAmount,
                    i,
                    contextData,
                    state,
                );
            }
        }
        return valueFields;
    }

    /**
     * When a line discount amount (discountNValue) is edited, derive that tier's % and rebuild all tier amounts.
     * Mutates `product` in place (percents + all discountNValue).
     */
    syncLineDiscountsFromValueEdit(product, salesOrderData, state, changedField) {
        if (!this.salesOrderAmountDiscountsEnabled(state)) return;
        const tierMatch = /^discount([1-4])Value$/.exec(changedField || '');
        if (!tierMatch) return;
        const tier = parseInt(tierMatch[1], 10);
        const userValueKey = `discount${tier}Value`;
        const userValueRaw = product[userValueKey];
        const contextData = this.getLineContextData(salesOrderData);
        const quantity = roundDecimalByConfig({
            value: product.quantity,
            type: DECIMAL_TYPES.QUANTITY,
            idEnvironment: contextData.idEnvironment,
            idCurrency: contextData.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId: 'quantity',
        });
        const price = roundDecimalByConfig({
            value: product.price,
            type: DECIMAL_TYPES.CURRENCY_PRICES,
            idEnvironment: contextData.idEnvironment,
            idCurrency: contextData.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId: 'price',
        });
        const B0 =
            this.parseDecimalValue(quantity) *
            this.getLineEffectiveUnitPrice({ ...product, price });

        const d = [1, 2, 3, 4].map((i) => product[`discount${i}`]);
        const bases = this.getSequentialDiscountBasesBeforeEachTier(B0, d[0], d[1], d[2], d[3]);
        const baseBefore = bases[tier - 1];
        const newVal = product[`discount${tier}Value`];

        if (newVal === null || newVal === '' || newVal === undefined) {
            d[tier - 1] = null;
        } else if (baseBefore > 0) {
            const rawPct = (this.parseDecimalValue(newVal) / baseBefore) * 100;
            d[tier - 1] = roundDecimalByConfig({
                value: rawPct,
                type: DECIMAL_TYPES.GENERAL,
                idEnvironment: contextData.idEnvironment,
                idCurrency: contextData.idCurrency,
                entityName: 'SALESORDERSLINES',
                fieldId: `discount${tier}`,
            });
        }

        for (let i = 1; i <= 4; i += 1) {
            product[`discount${i}`] = d[i - 1];
        }
        this.applyLineDiscountPercentClamp(product);
        for (let i = 0; i < 4; i += 1) {
            d[i] = product[`discount${i + 1}`];
        }

        const basesFinal = this.getSequentialDiscountBasesBeforeEachTier(
            B0,
            d[0],
            d[1],
            d[2],
            d[3],
        );
        for (let i = 1; i <= 4; i += 1) {
            const di = this.parseDecimalValue(d[i - 1]);
            if (!di) {
                product[`discount${i}Value`] = null;
            } else {
                const rawAmount = basesFinal[i - 1] * (di / 100);
                product[`discount${i}Value`] = this.roundLineDiscountValueAmount(
                    rawAmount,
                    i,
                    contextData,
                    state,
                );
            }
        }
    }

    getSequentialDiscountBasesBeforeEachTier(B0, discount1, discount2, discount3, discount4) {
        const bases = [];
        let b = this.parseDecimalValue(B0);
        const factors = [discount1, discount2, discount3, discount4].map((d) =>
            this.parseDecimalValue(d),
        );
        for (let i = 0; i < 4; i += 1) {
            bases.push(b);
            b *= 1 - factors[i] / 100;
        }
        return bases;
    }

    applyHeaderDiscountPercentClamp(data) {
        let capped = false;
        for (let i = 1; i <= 4; i += 1) {
            const key = `discount${i}`;
            const raw = this.parseDecimalValue(data[key]);
            if (raw < 0) {
                data[key] = 0;
                capped = true;
            } else if (raw > 100) {
                data[key] = 100;
                capped = true;
            }
        }
        if (capped) {
            warningToast({ text: getLiteral('label_discount_amount_cannot_exceed_total') });
        }
        return capped;
    }

    buildHeaderDiscountValueFieldsFromPercents(dataCrud, state, options = {}) {
        const contextData = this.getLineContextData(dataCrud);
        const calc = this.calculateEntityProducts({
            products: dataCrud.products || [],
            discount1: dataCrud.discount1,
            discount2: dataCrud.discount2,
            discount3: dataCrud.discount3,
            discount4: dataCrud.discount4,
            currencySymbol: dataCrud.currencySymbol,
            idBranch: dataCrud.idBranch,
            idCurrency: dataCrud.idCurrency,
        });
        const B0 = this.parseDecimalValue(calc.subtotal);
        this.applyHeaderDiscountPercentClamp(dataCrud);
        const d1 = dataCrud.discount1;
        const d2 = dataCrud.discount2;
        const d3 = dataCrud.discount3;
        const d4 = dataCrud.discount4;
        const bases = this.getSequentialDiscountBasesBeforeEachTier(B0, d1, d2, d3, d4);
        const valueFields = {};
        const tiersToRefresh =
            options.percentTiersToRefresh?.length > 0
                ? new Set(options.percentTiersToRefresh)
                : null;
        for (let i = 1; i <= 4; i += 1) {
            const valKey = `discount${i}Value`;
            if (tiersToRefresh && !tiersToRefresh.has(i)) {
                valueFields[valKey] = dataCrud[valKey] ?? null;
                continue;
            }
            const di = this.parseDecimalValue(dataCrud[`discount${i}`]);
            if (!di) {
                valueFields[valKey] = null;
            } else {
                const rawAmount = bases[i - 1] * (di / 100);
                valueFields[valKey] = this.roundHeaderDiscountValueAmount(
                    rawAmount,
                    i,
                    contextData,
                    state,
                );
            }
        }
        return valueFields;
    }

    /**
     * When opening CRUD: if discountNValue is set but discountN is missing/zero, derive % from amount.
     */
    fillMissingHeaderDiscountPercentsInCrud(crud, state = Context.store.getState()) {
        if (!this.salesOrderAmountDiscountsEnabled(state)) return;
        const calc = this.calculateEntityProducts({
            products: crud.products || [],
            discount1: crud.discount1,
            discount2: crud.discount2,
            discount3: crud.discount3,
            discount4: crud.discount4,
            currencySymbol: crud.currencySymbol,
            idBranch: crud.idBranch,
            idCurrency: crud.idCurrency,
        });
        const B0 = this.parseDecimalValue(calc.subtotal);
        const contextData = this.getLineContextData(crud);
        const d = [1, 2, 3, 4].map((i) => this.parseDecimalValue(crud[`discount${i}`]));

        for (let tier = 1; tier <= 4; tier += 1) {
            const valKey = `discount${tier}Value`;
            const rawVal = crud[valKey];
            const hasValue = !(rawVal === null || rawVal === undefined || rawVal === '');
            const hasPercent = d[tier - 1] !== 0;
            if (hasPercent || !hasValue) continue;

            const bases = this.getSequentialDiscountBasesBeforeEachTier(B0, d[0], d[1], d[2], d[3]);
            const baseBefore = bases[tier - 1];
            if (baseBefore <= 0) continue;

            const rawPct = (this.parseDecimalValue(rawVal) / baseBefore) * 100;
            d[tier - 1] = roundDecimalByConfig({
                value: rawPct,
                type: DECIMAL_TYPES.GENERAL,
                idEnvironment: contextData.idEnvironment,
                idCurrency: contextData.idCurrency,
                entityName: 'SALESORDERS',
                fieldId: `discount${tier}`,
            });
            crud[`discount${tier}`] = d[tier - 1];
        }
    }

    syncSalesOrderHeaderDiscountsAndTotals(newFields, dataCrud, state) {
        const mergedForCalc = { ...dataCrud, ...newFields };
        const contextData = this.getLineContextData(mergedForCalc);
        const calc = this.calculateEntityProducts({
            products: mergedForCalc.products || [],
            discount1: mergedForCalc.discount1,
            discount2: mergedForCalc.discount2,
            discount3: mergedForCalc.discount3,
            discount4: mergedForCalc.discount4,
            currencySymbol: mergedForCalc.currencySymbol,
            idBranch: mergedForCalc.idBranch,
            idCurrency: mergedForCalc.idCurrency,
        });
        const B0 = this.parseDecimalValue(calc.subtotal);

        const d = [1, 2, 3, 4].map((i) =>
            newFields.hasOwnProperty(`discount${i}`)
                ? newFields[`discount${i}`]
                : dataCrud[`discount${i}`],
        );

        for (let i = 1; i <= 4; i += 1) {
            const valKey = `discount${i}Value`;
            if (!newFields.hasOwnProperty(valKey)) continue;

            const bases = this.getSequentialDiscountBasesBeforeEachTier(B0, d[0], d[1], d[2], d[3]);
            const baseBefore = bases[i - 1];
            const newVal = newFields[valKey];

            if (newVal === null || newVal === '' || newVal === undefined) {
                d[i - 1] = null;
            } else if (baseBefore > 0) {
                const rawPct = (this.parseDecimalValue(newVal) / baseBefore) * 100;
                d[i - 1] = roundDecimalByConfig({
                    value: rawPct,
                    type: DECIMAL_TYPES.GENERAL,
                    idEnvironment: contextData.idEnvironment,
                    idCurrency: contextData.idCurrency,
                    entityName: 'SALESORDERS',
                    fieldId: `discount${i}`,
                });
            }
        }

        const patch = {
            discount1: d[0],
            discount2: d[1],
            discount3: d[2],
            discount4: d[3],
        };
        this.applyHeaderDiscountPercentClamp(patch);
        for (let i = 0; i < 4; i += 1) {
            d[i] = patch[`discount${i + 1}`];
        }

        const basesFinal = this.getSequentialDiscountBasesBeforeEachTier(
            B0,
            d[0],
            d[1],
            d[2],
            d[3],
        );
        for (let i = 1; i <= 4; i += 1) {
            const valKey = `discount${i}Value`;
            const di = this.parseDecimalValue(d[i - 1]);
            if (!di) {
                patch[valKey] = null;
            } else {
                const rawAmount = basesFinal[i - 1] * (di / 100);
                patch[valKey] = this.roundHeaderDiscountValueAmount(
                    rawAmount,
                    i,
                    contextData,
                    state,
                );
            }
        }

        const dataForTotals = {
            ...dataCrud,
            ...newFields,
            ...patch,
        };

        Object.assign(
            patch,
            this.getCalculatedAmountFields(dataForTotals, dataForTotals.products || []),
        );

        return patch;
    }

    parseDecimalValue(value) {
        if (value === null || value === undefined || value === '') return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

        const normalizedValue = String(value).replace(',', '.');
        const parsedValue = Number(normalizedValue);

        return Number.isFinite(parsedValue) ? parsedValue : 0;
    }

    // Unit price used for line totals. `price` tracks the edited line price:
    // - When the relation has a specialPrice, we reset `price` to that value on UoM
    //   change, so totals are a plain price × quantity (factor is bypassed).
    // - When the relation has no specialPrice, `price` stays as the product's
    //   `productPrice` and the UoM factor is applied here.
    getLineEffectiveUnitPrice(line) {
        const price = this.parseDecimalValue(line?.price);
        let relation = line?.unitOfMeasureRelation || null;
        if (!relation && line?.idRelationUoM && Array.isArray(line?.productUnitRelations)) {
            relation =
                findProductUnitRelationByStoredUomField(
                    line.productUnitRelations,
                    line.idRelationUoM,
                ) || null;
        }
        if (!relation) return price;
        if (getBackendBoolean(relation.useSpecialPrice) && relation.specialPrice != null) {
            return price;
        }
        const factor = this.parseDecimalValue(relation.factor) || 1;
        return price * factor;
    }

    normalizeTaxRateId(value) {
        if (value && typeof value === 'object') {
            value = value.value ?? value.id ?? null;
        }

        if (value === null || value === undefined || value === '' || value === '-1') return null;

        const parsedValue = parseInt(value, 10);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    }

    /**
     * tblTaxRate row where percentage parses to 0 (e.g. "Sin IVA" with value "4", not id 0).
     * Uses `percentage` or `ExtraInfo`; label fallback only for "N %" in text.
     */
    getDefaultZeroTaxRateId(state = Context.store.getState()) {
        const taxRates = state?.serverList?.tblTaxRate?.data || [];
        const zeroTaxRate = taxRates.find((taxRate) => {
            const explicitPercentage = taxRate?.percentage ?? taxRate?.ExtraInfo ?? null;
            if (
                explicitPercentage !== null &&
                explicitPercentage !== undefined &&
                explicitPercentage !== ''
            ) {
                return this.parseDecimalValue(explicitPercentage) === 0;
            }

            const label = this.getTaxRateLabel(taxRate, '');
            const match = `${label}`.match(/(\d+(?:[.,]\d+)?)\s*%/);
            return !!match && this.parseDecimalValue(match[1]) === 0;
        });

        return this.normalizeTaxRateId(zeroTaxRate?.value ?? zeroTaxRate?.id ?? null);
    }

    resolveTaxRateId(value, state = Context.store.getState()) {
        const normalizedTaxRateId = this.normalizeTaxRateId(value);
        if (normalizedTaxRateId !== null) return normalizedTaxRateId;

        // Normalize empty values to the default 0% option.
        return this.getDefaultZeroTaxRateId(state);
    }

    /**
     * Whether the selected account forces 0% VAT on lines. Inferred from companyrates idCompany.ExtraInfo5
     * when the full fuzzy option is present; otherwise uses salesOrderCompanyTaxExempt set in changeFields
     * when the company is selected (CRUD often keeps only { label, value } on idCompany).
     */
    isSalesOrderCompanyTaxExempt(salesOrderData, state = Context.store.getState()) {
        if (!state?.config?.userData?.useTaxesSalesOrders) return false;

        if (
            Object.prototype.hasOwnProperty.call(salesOrderData ?? {}, 'salesOrderCompanyTaxExempt')
        ) {
            return getBackendBoolean(salesOrderData.salesOrderCompanyTaxExempt);
        }

        const idCompany = salesOrderData?.idCompany;
        if (!idCompany || typeof idCompany !== 'object') return false;

        return getBackendBoolean(idCompany.ExtraInfo5);
    }

    getResolvedZeroVatLineTax(state = Context.store.getState()) {
        const zeroId = this.getDefaultZeroTaxRateId(state);
        const taxRateItem = this.getTaxRateCatalogItem(zeroId, state);
        return {
            taxRateId: zeroId,
            taxRate: this.getTaxRateLabel(taxRateItem, ''),
        };
    }

    /**
     * Default tax when adding a sales order line. If idCompany infers tax exempt (ExtraInfo5), 0%
     * from the start; else product → environment (tblSucursales) → CRM default → resolveTaxRateId.
     */
    resolveInitialLineTaxForNewLine({
        modalProduct,
        defaultValueFields,
        lineIdEnvironment,
        salesOrderData,
        state = Context.store.getState(),
    }) {
        if (this.isSalesOrderCompanyTaxExempt(salesOrderData, state)) {
            return this.getResolvedZeroVatLineTax(state);
        }

        const productTaxId = this.normalizeTaxRateId(modalProduct?.defaultTaxRateId);
        if (productTaxId !== null) {
            const taxRateItem = this.getTaxRateCatalogItem(productTaxId, state);
            return {
                taxRateId: productTaxId,
                taxRate: modalProduct?.defaultTaxRate || this.getTaxRateLabel(taxRateItem, ''),
            };
        }

        if (
            lineIdEnvironment !== null &&
            lineIdEnvironment !== undefined &&
            lineIdEnvironment !== ''
        ) {
            const branches = state?.serverList?.tblSucursales?.data || [];
            const row = branches.find((b) => String(b['-id']) === String(lineIdEnvironment));
            const envTaxId = this.normalizeTaxRateId(row?.defaulttaxrateid);
            if (envTaxId !== null) {
                const taxRateItem = this.getTaxRateCatalogItem(envTaxId, state);
                return {
                    taxRateId: envTaxId,
                    taxRate: this.getTaxRateLabel(taxRateItem, ''),
                };
            }
        }

        const schemaTaxId = this.normalizeTaxRateId(defaultValueFields?.taxrateid);
        if (schemaTaxId !== null) {
            const taxRateItem = this.getTaxRateCatalogItem(schemaTaxId, state);
            return {
                taxRateId: schemaTaxId,
                taxRate: this.getTaxRateLabel(taxRateItem, ''),
            };
        }

        return { taxRateId: null, taxRate: '' };
    }

    getTaxRateCatalogItem(taxRateId, state = Context.store.getState()) {
        if (taxRateId === null || taxRateId === undefined) return null;

        const taxRates = state?.serverList?.tblTaxRate?.data || [];
        return (
            taxRates.find(
                (taxRate) => String(taxRate?.value ?? taxRate?.id) === String(taxRateId),
            ) || null
        );
    }

    getTaxRateLabel(taxRateItem, fallbackLabel = '') {
        return (
            taxRateItem?.label ||
            taxRateItem?.MatchingInfo ||
            taxRateItem?.description ||
            taxRateItem?.strdescription ||
            fallbackLabel ||
            ''
        );
    }

    getTaxRatePercentage(taxRateItem, fallbackLabel = '') {
        const parsedPercentage = this.parseDecimalValue(
            taxRateItem?.percentage ?? taxRateItem?.ExtraInfo ?? null,
        );

        if (parsedPercentage) return parsedPercentage;

        const match = `${fallbackLabel}`.match(/(\d+(?:[.,]\d+)?)\s*%/);
        if (!match) return 0;

        return this.parseDecimalValue(match[1]);
    }

    roundLineAmount(value, fieldId, contextData) {
        return roundDecimalByConfig({
            value,
            type: DECIMAL_TYPES.CURRENCY_PRICES,
            idEnvironment: contextData?.idEnvironment,
            idCurrency: contextData?.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId,
        });
    }

    roundSalesOrderAmount(value, fieldId, contextData) {
        return roundDecimalByConfig({
            value,
            type: DECIMAL_TYPES.CURRENCY_GENERAL,
            idEnvironment: contextData?.idEnvironment,
            idCurrency: contextData?.idCurrency,
            entityName: 'SALESORDERS',
            fieldId,
        });
    }

    formatSalesOrderAmount(value, fieldId, contextData) {
        return formatDecimalByConfig({
            value,
            type: DECIMAL_TYPES.CURRENCY_GENERAL,
            idEnvironment: contextData?.idEnvironment,
            idCurrency: contextData?.idCurrency,
            entityName: 'SALESORDERS',
            fieldId,
        });
    }

    recalculateProductLine(
        product,
        salesOrderData,
        state = Context.store.getState(),
        options = {},
    ) {
        const amountDiscounts = this.salesOrderAmountDiscountsEnabled(state);
        let lineSource = product;
        if (options?.fillMissingOnly && amountDiscounts) {
            lineSource = {
                ...product,
                ...this.mergeLineDiscountPairsForFillMissing(product, salesOrderData, state),
            };
        }

        const contextData = this.getLineContextData(salesOrderData);
        const useTaxesSalesOrders = state?.config?.userData?.useTaxesSalesOrders || false;
        const uomEnabled = !!state?.config?.userData?.uomEnabled;

        // UoM: memoize the resolved relation on the line so subsequent recalcs skip
        // the find(). We intentionally do NOT mutate `price` — the unit-price column
        // always shows the product's base price; the factor is applied only to the
        // line total via getLineEffectiveUnitPrice below.
        if (uomEnabled) {
            const relation =
                lineSource.unitOfMeasureRelation ||
                findProductUnitRelationByStoredUomField(
                    lineSource.productUnitRelations || [],
                    lineSource.idRelationUoM,
                ) ||
                null;
            if (relation && relation !== lineSource.unitOfMeasureRelation) {
                lineSource = { ...lineSource, unitOfMeasureRelation: relation };
            }
        }

        const quantity = roundDecimalByConfig({
            value: lineSource.quantity,
            type: DECIMAL_TYPES.QUANTITY,
            idEnvironment: contextData.idEnvironment,
            idCurrency: contextData.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId: 'quantity',
        });
        const price = roundDecimalByConfig({
            value: lineSource.price,
            type: DECIMAL_TYPES.CURRENCY_PRICES,
            idEnvironment: contextData.idEnvironment,
            idCurrency: contextData.idCurrency,
            entityName: 'SALESORDERSLINES',
            fieldId: 'price',
        });
        // UoM minStep validation: quantity must be an exact multiple of minStep.
        // Uses the configured quantity decimals to scale to integer arithmetic so
        // the check is immune to floating-point drift and naturally respects the
        // precision cap defined by the decimals config (precisions smaller than
        // the config decimals fall back to "no validation").
        if (uomEnabled) {
            const relation = lineSource.unitOfMeasureRelation || null;
            const minStep = this.parseDecimalValue(relation?.minStep);
            const qtyNum = this.parseDecimalValue(quantity);
            if (minStep > 0 && qtyNum !== 0) {
                const decimals = getDecimalsByConfig({
                    type: DECIMAL_TYPES.QUANTITY,
                    idEnvironment: contextData.idEnvironment,
                    idCurrency: contextData.idCurrency,
                    entityName: 'SALESORDERSLINES',
                    fieldId: 'quantity',
                });
                const scale = Math.pow(10, Math.max(0, decimals || 0));
                const scaledQty = Math.round(qtyNum * scale);
                const scaledStep = Math.round(minStep * scale);
                lineSource._minStepError = scaledStep > 0 && scaledQty % scaledStep !== 0;
            } else {
                lineSource._minStepError = false;
            }
        } else if (lineSource._minStepError) {
            lineSource._minStepError = false;
        }

        const discounts = [1, 2, 3, 4].reduce((result, discountIndex) => {
            const fieldId = `discount${discountIndex}`;
            const fieldValue = lineSource[fieldId];

            result[fieldId] =
                fieldValue === undefined || fieldValue === null || fieldValue === ''
                    ? 0
                    : roundDecimalByConfig({
                          value: fieldValue,
                          type: DECIMAL_TYPES.GENERAL,
                          idEnvironment: contextData.idEnvironment,
                          idCurrency: contextData.idCurrency,
                          entityName: 'SALESORDERSLINES',
                          fieldId,
                      });

            return result;
        }, {});

        if (amountDiscounts) {
            this.applyLineDiscountPercentClamp(discounts);
        }

        const totalDiscount = this.calculateTotalDiscount(
            discounts.discount1,
            discounts.discount2,
            discounts.discount3,
            discounts.discount4,
        );
        // Line gross subtotal: quantity * effective unit price (base price × UoM
        // factor, or relation's specialPrice). `price` itself stays unchanged.
        const effectiveUnitPrice = this.getLineEffectiveUnitPrice({ ...lineSource, price });
        const subtotalAmount = this.roundLineAmount(
            quantity * effectiveUnitPrice,
            'subtotalAmount',
            contextData,
        );
        const subtotalAmountValue = this.parseDecimalValue(subtotalAmount);
        // Line net base after line-level discounts. Use gross amount (not rounded subtotal)
        // and `price` decimals — subtotalAmount defaults to 2 decimals and would truncate TOTAL.
        const grossLineAmount =
            this.parseDecimalValue(quantity) * this.parseDecimalValue(effectiveUnitPrice);
        const finalprice = this.roundLineAmount(
            grossLineAmount * totalDiscount,
            'price',
            contextData,
        );
        const finalpriceValue = this.parseDecimalValue(finalprice);

        if (!useTaxesSalesOrders) {
            const next = {
                ...lineSource,
                quantity,
                price,
                ...discounts,
                subtotalAmount,
                finalprice,
            };
            if (amountDiscounts) {
                if (options?.fillMissingOnly) {
                    Object.assign(
                        next,
                        this.mergeLineDiscountPairsForFillMissing(next, salesOrderData, state),
                    );
                } else {
                    Object.assign(
                        next,
                        this.buildLineDiscountValueFieldsFromPercents(next, salesOrderData, state),
                    );
                }
            }
            return next;
        }

        let normalizedTaxRateId = this.resolveTaxRateId(lineSource.taxRateId, state);
        if (
            this.isSalesOrderCompanyTaxExempt(salesOrderData, state) &&
            !lineSource.salesOrderLineTaxManual
        ) {
            normalizedTaxRateId = this.getDefaultZeroTaxRateId(state);
        }
        const taxRateItem = this.getTaxRateCatalogItem(normalizedTaxRateId, state);
        const taxRate = this.getTaxRateLabel(
            taxRateItem,
            lineSource.taxRate || lineSource.defaultTaxRate || '',
        );
        const lineDiscountTotalAmount = this.roundLineAmount(
            subtotalAmountValue - finalpriceValue,
            'subtotalAmount',
            contextData,
        );

        // Product lines CRUD uses singleValueList Select: value must be { label, value } or the
        // control shows empty despite a resolved 0% / exempt rate.
        const taxRateIdForCrud =
            normalizedTaxRateId !== null && normalizedTaxRateId !== undefined
                ? { label: taxRate, value: String(normalizedTaxRateId) }
                : null;

        const next = {
            ...lineSource,
            quantity,
            price,
            ...discounts,
            finalprice,
            subtotalAmount,
            lineDiscountTotalAmount,
            taxRateId: taxRateIdForCrud,
            taxRate,
            totalAmount: finalprice,
        };
        delete next.taxAmount;
        if (amountDiscounts) {
            if (options?.fillMissingOnly) {
                Object.assign(
                    next,
                    this.mergeLineDiscountPairsForFillMissing(next, salesOrderData, state),
                );
            } else {
                Object.assign(
                    next,
                    this.buildLineDiscountValueFieldsFromPercents(next, salesOrderData, state),
                );
            }
        }
        return next;
    }

    getCalculatedAmountFields(data, products, calculatedData = null) {
        const contextData = this.getLineContextData(data);
        const useTaxesSalesOrders =
            Context.store.getState()?.config?.userData?.useTaxesSalesOrders || false;
        const salesOrderShippingCost =
            !!Context.store.getState()?.config?.userData?.salesOrderShippingCost;
        const result =
            calculatedData ||
            this.calculateEntityProducts(
                {
                    ...data,
                    products,
                },
                'edit',
            );
        // In taxes mode, the document total shown/saved is finalTotalAmount
        // (sum of adjusted line bases + adjusted line taxes).
        const totalValue = useTaxesSalesOrders ? result.finalTotalAmount : result.total;

        const fields = {
            products: result.products,
            subtotalRaw: result.subtotal,
            subtotal: this.formatSalesOrderAmount(result.subtotal, 'subtotal', contextData),
            totalRaw: totalValue,
            total: this.formatSalesOrderAmount(totalValue, 'totalAmount', contextData),
        };

        if (useTaxesSalesOrders) {
            fields.subtotalAmount = result.subtotalAmount;
            fields.discountTotalAmount = result.discountTotalAmount;
            fields.taxableBaseAmount = result.taxableBaseAmount;
            fields.taxTotalAmount = result.taxTotalAmount;
            fields.finalTotalAmount = result.finalTotalAmount;
        } else {
            // Legacy document total (fieldConfiguration totalAmount / API amountSalesOrder).
            fields.amountSalesOrder = totalValue;
        }

        if (salesOrderShippingCost) {
            fields.shippingCost = this.calculateShippingCost(data, result.products);
        }

        return fields;
    }

    calculateTotalDiscount(discount1, discount2, discount3, discount4) {
        // Composite discount factor:
        // (1-d1) * (1-d2) * (1-d3) * (1-d4)
        // where dN are discount percentages.
        const normalizedDiscount1 = this.parseDecimalValue(discount1);
        const normalizedDiscount2 = this.parseDecimalValue(discount2);
        const normalizedDiscount3 = this.parseDecimalValue(discount3);
        const normalizedDiscount4 = this.parseDecimalValue(discount4);

        let totalDiscount = 1;
        if (normalizedDiscount1) totalDiscount *= 1 - normalizedDiscount1 / 100;
        if (normalizedDiscount2) totalDiscount *= 1 - normalizedDiscount2 / 100;
        if (normalizedDiscount3) totalDiscount *= 1 - normalizedDiscount3 / 100;
        if (normalizedDiscount4) totalDiscount *= 1 - normalizedDiscount4 / 100;
        totalDiscount = 1 - (1 - totalDiscount);
        return totalDiscount;
    }

    calculateShippingCost(salesOrderData, orderLines) {
        const rawShippingCost =
            'shippingCostRaw' in (salesOrderData ?? {}) ? salesOrderData.shippingCostRaw : null;
        const connectorValue = this.parseDecimalValue(rawShippingCost);
        if (rawShippingCost !== null && rawShippingCost !== undefined && rawShippingCost !== '') {
            return connectorValue;
        }

        if (orderLines?.length) {
            let transportTotal = 0;
            let allLinesTotal = 0;
            const transportLines = [];
            for (const line of orderLines) {
                const lineAmount = this.parseDecimalValue(
                    line.finalprice ?? line.entity?.finalprice,
                );
                allLinesTotal += lineAmount;
                if ((line.productTypeCode ?? line.entity?.productTypeCode) === 'transport') {
                    transportTotal += lineAmount;
                    transportLines.push(line);
                }
            }
            if (transportTotal !== 0) {
                let discountedTransportTotal;
                // With amount-based discounts the effective factor depends on the
                // current subtotal, so the proportional share of each line changes
                // when any line is modified.
                if (this.salesOrderAmountDiscountsEnabled() && allLinesTotal > 0) {
                    const totalDiscountAmount = this.getHeaderAmountDiscountTotal(
                        salesOrderData,
                        allLinesTotal,
                    );
                    const transportShare = transportTotal / allLinesTotal;
                    discountedTransportTotal = this.parseDecimalValue(
                        transportTotal - totalDiscountAmount * transportShare,
                    );
                } else {
                    // Percentage-based discounts: same factor for every line.
                    const headerDiscountFactor = this.getHeaderDiscountFactor(salesOrderData);
                    discountedTransportTotal = this.parseDecimalValue(
                        transportTotal * headerDiscountFactor,
                    );
                }

                const useTaxesSalesOrders =
                    !!Context.store.getState()?.config?.userData?.useTaxesSalesOrders;
                if (!useTaxesSalesOrders) return discountedTransportTotal;

                // Taxes mode: lines are not recalculated here, so add transport tax
                // separately, distributing the header-adjusted base proportionally.
                return (
                    discountedTransportTotal +
                    this.calculateTransportTaxTotal(
                        transportLines,
                        transportTotal,
                        discountedTransportTotal,
                        salesOrderData,
                    )
                );
            }
        }

        return 0;
    }

    calculateTransportTaxTotal(
        transportLines,
        transportTotal,
        discountedTransportTotal,
        salesOrderData,
    ) {
        if (!transportTotal) return 0;
        const state = Context.store.getState();
        const contextData = this.getLineContextData(salesOrderData);
        const companyExempt = this.isSalesOrderCompanyTaxExempt(salesOrderData, state);

        return transportLines.reduce((acc, line) => {
            const lineAmount = this.parseDecimalValue(line.finalprice ?? line.entity?.finalprice);
            if (!lineAmount) return acc;

            const adjustedBase = (lineAmount / transportTotal) * discountedTransportTotal;
            const taxRateId =
                companyExempt && !line.salesOrderLineTaxManual
                    ? this.getDefaultZeroTaxRateId(state)
                    : this.resolveTaxRateId(line.taxRateId, state);
            const taxPercentage = this.getTaxRatePercentage(
                this.getTaxRateCatalogItem(taxRateId, state),
                line.taxRate || line.defaultTaxRate || '',
            );

            return (
                acc +
                this.parseDecimalValue(
                    this.roundLineAmount(
                        adjustedBase * (taxPercentage / 100),
                        'taxAmount',
                        contextData,
                    ),
                )
            );
        }, 0);
    }

    getHeaderAmountDiscountTotal(salesOrderData, subtotal) {
        // Sequential discount amounts: each tier reduces the base for the next.
        const d = [
            salesOrderData?.discount1,
            salesOrderData?.discount2,
            salesOrderData?.discount3,
            salesOrderData?.discount4,
        ].map((v) => this.parseDecimalValue(v));
        let remaining = this.parseDecimalValue(subtotal);
        let totalDiscount = 0;
        for (let i = 0; i < 4; i += 1) {
            if (!d[i]) continue;
            const tierAmount = remaining * (d[i] / 100);
            totalDiscount += tierAmount;
            remaining -= tierAmount;
        }
        return totalDiscount;
    }

    getHeaderDiscountFactor(salesOrderData = {}) {
        // Header discounts are never split by line count. Applying this single
        // factor to each line base produces proportional distribution by amount.
        return this.calculateTotalDiscount(
            salesOrderData?.discount1,
            salesOrderData?.discount2,
            salesOrderData?.discount3,
            salesOrderData?.discount4,
        );
    }

    getAdjustedTaxLineAmounts(line, { headerDiscountFactor, contextData, state, salesOrderData }) {
        const lineTaxBase = this.parseDecimalValue(line.finalprice);
        // Header discounts first adjust the line tax base, then tax is recalculated.
        const adjustedTaxBase = this.parseDecimalValue(
            this.roundLineAmount(lineTaxBase * headerDiscountFactor, 'finalPrice', contextData),
        );

        let normalizedTaxRateId = this.resolveTaxRateId(line.taxRateId, state);
        if (
            this.isSalesOrderCompanyTaxExempt(salesOrderData, state) &&
            !line.salesOrderLineTaxManual
        ) {
            normalizedTaxRateId = this.getDefaultZeroTaxRateId(state);
        }
        const taxRateItem = this.getTaxRateCatalogItem(normalizedTaxRateId, state);
        const taxPercentage = this.getTaxRatePercentage(
            taxRateItem,
            line.taxRate || line.defaultTaxRate || '',
        );

        // When tax percentage is null/empty/0 we must recalculate as 0%,
        // never keep a stale previous tax amount.
        const adjustedTaxAmountRaw = adjustedTaxBase * (taxPercentage / 100);
        const adjustedTaxAmount = this.parseDecimalValue(
            this.roundLineAmount(adjustedTaxAmountRaw, 'taxAmount', contextData),
        );
        const adjustedTotalAmount = this.parseDecimalValue(
            this.roundLineAmount(adjustedTaxBase + adjustedTaxAmount, 'totalAmount', contextData),
        );

        return {
            adjustedTaxBase,
            adjustedTaxAmount,
            adjustedTotalAmount,
        };
    }

    calculateTaxedDocumentTotals(
        orderLines,
        salesOrderData,
        state = Context.store.getState(),
        mutateLines = false,
    ) {
        // Canonical totals flow for taxes-enabled Sales Orders:
        // subtotalAmount      = sum(line gross subtotal)
        // taxableBaseAmount   = sum(line adjusted tax base)
        // taxTotalAmount      = sum(line recalculated tax)
        // finalTotalAmount    = sum(line adjusted base + recalculated tax)
        const contextData = this.getLineContextData(salesOrderData);
        const headerDiscountFactor = this.getHeaderDiscountFactor(salesOrderData);

        let grossSubtotalAmount = 0;
        let taxableBaseAmount = 0;
        let taxTotalAmount = 0;
        let finalTotalAmount = 0;

        orderLines.forEach((line) => {
            grossSubtotalAmount += this.parseDecimalValue(line.subtotalAmount);

            const { adjustedTaxBase, adjustedTaxAmount, adjustedTotalAmount } =
                this.getAdjustedTaxLineAmounts(line, {
                    headerDiscountFactor,
                    contextData,
                    state,
                    salesOrderData,
                });

            if (mutateLines) {
                // Payload: line total = net after line discounts (not header-adjusted base + tax).
                // Line tax is not sent to BE; header totals still use adjustedTaxBase/Amount internally.
                delete line.taxAmount;
                line.totalAmount = this.roundLineAmount(
                    this.parseDecimalValue(line.finalprice),
                    'finalPrice',
                    contextData,
                );
            }

            taxableBaseAmount += adjustedTaxBase;
            taxTotalAmount += adjustedTaxAmount;
            finalTotalAmount += adjustedTotalAmount;
        });

        const roundedSubtotalAmount = this.roundSalesOrderAmount(
            grossSubtotalAmount,
            'subtotalAmount',
            contextData,
        );
        const roundedTaxableBaseAmount = this.roundSalesOrderAmount(
            taxableBaseAmount,
            'taxableBaseAmount',
            contextData,
        );
        const roundedTaxTotalAmount = this.roundSalesOrderAmount(
            taxTotalAmount,
            'taxTotalAmount',
            contextData,
        );
        const roundedFinalTotalAmount = this.roundSalesOrderAmount(
            finalTotalAmount,
            'finalTotalAmount',
            contextData,
        );
        const roundedDiscountTotalAmount = this.roundSalesOrderAmount(
            roundedSubtotalAmount - roundedTaxableBaseAmount,
            'discountTotalAmount',
            contextData,
        );

        return {
            subtotalAmount: roundedSubtotalAmount,
            discountTotalAmount: roundedDiscountTotalAmount,
            taxableBaseAmount: roundedTaxableBaseAmount,
            taxTotalAmount: roundedTaxTotalAmount,
            finalTotalAmount: roundedFinalTotalAmount,
        };
    }

    getProductsSchema = (schema) => {
        return schema.find((item) => item?.fields?.[0]?.id === 'products')?.fields?.[0]?.schema;
    };

    /** Applies header + line smart field defaults/overrides to product rows (same as grid display). */
    resolveProductsWithLineSmartFields = (products, productsSchema, parentData, state) => {
        if (!products?.length || !productsSchema?.length) {
            return products;
        }
        const activeCrud = getActiveCrud(state);
        const { rows } = applySalesOrderLinesSmartFields({
            crudSchema: productsSchema,
            rows: products,
            parentConfigMap: activeCrud?.configMap,
            parentData,
            isNewEntity: !parentData?.id,
        });
        return rows;
    };

    checkCustomErrors = (errors, schema, data) => {
        //Check productLines errors
        const productsSchema = this.getProductsSchema(schema);
        const productsData = data.products || [];
        let newErrors = {};
        let tableErrors = {};
        let firstErrorField = '';
        let customErrors = {
            ...errors,
            ...newErrors,
        };

        // Check if blockSalesOrdersWithoutProducts is enabled
        const state = Context.store.getState();
        const blockSalesOrdersWithoutProducts = getBackendBoolean(
            state?.config?.userData?.blockSalesOrdersWithoutProducts,
        );
        const multiEnvironment = state?.config?.permission?.multiEnvironment;

        // Don't show products validation modal if multiEnvironment is active and no environment selected
        const hasEnvironmentError = multiEnvironment && !data.idBranch;

        // Use same logic as disableEditSalesOrdersLines
        const standardFieldsSchemaSalesOrders =
            state?.config?.standardFieldsSchemaMap?.[SALESORDERS.extraFieldName];
        const activeCrud = getActiveCrud(state);
        const configMap = activeCrud?.configMap;
        const isNewEntity = !data.id;

        const smartFieldProps = getSmartFieldProps({
            field: standardFieldsSchemaSalesOrders?.addProductsButton,
            configMap: configMap,
            data,
            isNewEntity,
        });

        const disableEditSalesOrdersLines = Object.keys(smartFieldProps).length
            ? !!smartFieldProps.hidden
            : false;

        if (
            blockSalesOrdersWithoutProducts &&
            (!productsData || productsData.length === 0) &&
            !hasEnvironmentError &&
            !disableEditSalesOrdersLines
        ) {
            customErrors.products = 'mandatory';
            firstErrorField = 'products';
            customErrors.showValidationModal = true;
            customErrors.validationTitle = getLiteral('label_popup_no_products_salesorder_title');
            customErrors.validationMessage = getLiteral('label_popup_no_products_salesorder');
            customErrors.firstErrorField = firstErrorField;
            return customErrors;
        }

        const { schema: resolvedProductsSchema, rows: resolvedProducts } =
            applySalesOrderLinesSmartFields({
                crudSchema: productsSchema,
                rows: productsData,
                parentConfigMap: configMap,
                parentData: data,
                isNewEntity,
            });

        resolvedProducts.forEach((product, rowIndex) => {
            resolvedProductsSchema.forEach((current) => {
                const fieldId = current.isExtra ? current.id.toLowerCase() : current.id;
                const lineOverrides =
                    product._lineSmartFieldOverrides?.[fieldId] ??
                    product._lineSmartFieldOverrides?.[current.id];

                if (!isLineFieldMandatory({ field: current, lineOverrides })) {
                    return;
                }

                if (isLineFieldValueEmpty(resolveLineFieldValue(product, current))) {
                    newErrors[current.id] = 'mandatory';
                    if (!tableErrors[current.id]) tableErrors[current.id] = { rows: {} };
                    tableErrors[current.id].rows[rowIndex] = {
                        error: 'mandatory',
                        colId: current.id,
                    };
                    if (isEmpty(errors) && !firstErrorField) firstErrorField = current.id;
                }
            });

            // UoM minStep violation surfaces on the quantity column.
            if (product?._minStepError) {
                if (!tableErrors.quantity) tableErrors.quantity = { rows: {} };
                tableErrors.quantity.rows[rowIndex] = {
                    error: 'minStep',
                    colId: 'quantity',
                };
                newErrors.quantity = 'minStep';
                if (isEmpty(errors) && !firstErrorField) firstErrorField = 'quantity';
            }
        });

        if (firstErrorField) {
            customErrors = {
                ...customErrors,
                firstErrorField,
                products: tableErrors,
            };
        }

        return customErrors;
    };

    beforeSave(schema, data) {
        if (!data) return data;
        const state = Context.store.getState();
        const useTaxesSalesOrders = state?.config?.userData?.useTaxesSalesOrders || false;

        data.extraFields = {};
        schema.forEach((tab) => {
            tab.fields?.forEach((field) => {
                if (!field.isExtra) return;
                if (data.hasOwnProperty(field.serverId)) {
                    data.extraFields[field.serverId] = data[field.serverId];
                    delete data[field.serverId];
                }
            });
        });

        if (data.products?.length > 0) {
            const productsSchema = this.getProductsSchema(schema);
            const resolvedProducts = this.resolveProductsWithLineSmartFields(
                data.products,
                productsSchema,
                data,
                state,
            );
            data.orderslines = resolvedProducts.map((product) => {
                let crudProduct = {
                    idSalesOrder: data.id,
                    extraFields: {},
                };

                // new line so id is the product id instead of this line id
                if (!product.isNew) {
                    crudProduct.entityId = product.id;
                }

                productsSchema.forEach((field) => {
                    const id = field.isExtra
                        ? field.serverId?.toLowerCase?.() || field.serverId
                        : product.hasOwnProperty(field.serverId)
                          ? field.serverId
                          : field.serverId.toLowerCase();

                    if (field.isExtra) {
                        switch (field.type) {
                            case 'date':
                            case 'dateTime': {
                                const formatted = formatLineDateForServer(
                                    resolveLineFieldValue(product, field),
                                );
                                crudProduct.extraFields[id] = formatted;
                                break;
                            }
                            case 'multipleValueList':
                            case 'fuzzySearchMultiple':
                            case 'multiSelect': {
                                const raw = resolveLineFieldValue(product, field);
                                if (!raw) {
                                    crudProduct.extraFields[id] = null;
                                    break;
                                }
                                if (Array.isArray(raw)) {
                                    const ids = raw
                                        .map((item) =>
                                            item && typeof item === 'object'
                                                ? (item.value ?? item.id)
                                                : item,
                                        )
                                        .filter(
                                            (item) =>
                                                item !== null && item !== undefined && item !== '',
                                        );
                                    crudProduct.extraFields[id] = ids.length
                                        ? `;${ids.map(String).join(';')};`
                                        : null;
                                    break;
                                }
                                if (typeof raw === 'object' && raw.value !== undefined) {
                                    crudProduct.extraFields[id] = raw.value;
                                    break;
                                }
                                crudProduct.extraFields[id] = raw;
                                break;
                            }
                            default: {
                                const raw = resolveLineFieldValue(product, field);
                                crudProduct.extraFields[id] =
                                    raw != null &&
                                    typeof raw === 'object' &&
                                    raw.value !== undefined
                                        ? raw.value
                                        : raw;
                                break;
                            }
                        }

                        return;
                    } else {
                        if (id === 'taxRateId' && typeof product[id] === 'object') {
                            // Persist normalized tax rate id; fallback to 0% option when empty.
                            crudProduct[id] = this.resolveTaxRateId(product[id]?.value, state);
                        } else if (id === 'taxRateId') {
                            // Persist normalized tax rate id; fallback to 0% option when empty.
                            crudProduct[id] = this.resolveTaxRateId(product[id], state);
                        } else {
                            crudProduct[id] = product[id];
                        }
                    }
                });

                // backend case insensitive...
                delete crudProduct.entityid;

                // Preserve the original product unit price even if the schema
                // does not expose it: backend relies on it to differentiate the
                // line price from the product price (UoM special-price flow).
                if (
                    product?.productPrice !== undefined &&
                    product?.productPrice !== null &&
                    crudProduct.productPrice === undefined
                ) {
                    crudProduct.productPrice = product.productPrice;
                }

                // Strip UoM helper-only fields; backend expects scalar idRelationUoM.
                delete crudProduct.productUnitRelations;
                delete crudProduct.unitOfMeasureRelation;
                delete crudProduct.unitOfMeasure;
                delete crudProduct._minStepError;
                delete crudProduct._lineSmartFieldOverrides;
                if (crudProduct.idRelationUoM && typeof crudProduct.idRelationUoM === 'object') {
                    crudProduct.idRelationUoM =
                        crudProduct.idRelationUoM.value ?? crudProduct.idRelationUoM.id ?? null;
                }
                crudProduct.idRelationUoM = nullIfBackendEmptyId(crudProduct.idRelationUoM);

                return crudProduct;
            });
            delete data.products;
        }

        data.amountSalesOrder = data.totalRaw;
        if (useTaxesSalesOrders) {
            const orderLines = data.orderslines || [];
            // Reuse the same totals engine used while editing rows so values
            // displayed in UI and values sent to backend are always identical.
            const totals = this.calculateTaxedDocumentTotals(orderLines, data, state, true);

            data.subtotalAmount = data.subtotalAmount ?? totals.subtotalAmount;
            data.taxableBaseAmount = data.taxableBaseAmount ?? totals.taxableBaseAmount;
            data.discountTotalAmount = data.discountTotalAmount ?? totals.discountTotalAmount;
            data.taxTotalAmount = data.taxTotalAmount ?? totals.taxTotalAmount;
            data.finalTotalAmount = data.finalTotalAmount ?? totals.finalTotalAmount;
            data.amountSalesOrder = data.finalTotalAmount;
        } else {
            // Legacy clients: persist only amountSalesOrder (totalAmount). Do not send new
            // tax aggregate fields so existing integrations and calculations stay unchanged.
            delete data.subtotalAmount;
            delete data.discountTotalAmount;
            delete data.taxableBaseAmount;
            delete data.taxTotalAmount;
            delete data.finalTotalAmount;
        }
        delete data.totalRaw;
        delete data.subtotalRaw;
        delete data.rawProducts;
        delete data.extraData;
        delete data.currency;
        delete data.currencySymbol;
        delete data.address;
        delete data.companyExtraInfo;
        delete data.salesOrderCompanyTaxExempt;
        delete data.shippingCost;

        // Same property names as SalesOrdersModel.createCrud / PUT body contract (avoid duplicate discountNvalue).
        for (let i = 1; i <= 4; i++) {
            const camel = `discount${i}Value`;
            const lower = `discount${i}value`;
            if (data[lower] !== undefined && data[camel] === undefined) {
                data[camel] = data[lower];
            }
            delete data[lower];
        }

        return data;
    }

    getEntity(id, success, error, withExtraFields = true) {
        const entity = new Promise((resolve, reject) => {
            this.getSalesOrder(id, resolve, reject, withExtraFields);
        });

        const entityProducts = new Promise((resolve, reject) => {
            this.getOrderProducts(id, resolve, reject);
        });

        Promise.all([entity, entityProducts]).then((result) => {
            const { entity, data } = result[0];
            if (!data) {
                error('the entity does not exists');
                return;
            }
            entity.products = result[1];
            data.products = result[1];
            success(entity, data);
        }, error);
    }

    getSalesOrder(id, success, error, withExtraFields = true) {
        this.context.domainManager.getEntity(
            id,
            SALESORDERS.entity,
            withExtraFields,
            (data) => {
                const entity = { ...data };
                this.context.extraFieldManager.getExtraFieldSchema(
                    SALESORDERS,
                    (extraFieldSchema) => {
                        const extraFields = [];
                        extraFieldSchema.forEach((section) => {
                            const wrapperSection = {
                                descripcion: section.descripcion,
                                id: section['-id'],
                                data: [],
                            };
                            section.tabFields.forEach((field) => {
                                field.descripcion = field.description;
                                let value = entity[field.id.toLowerCase()];
                                switch (field.dataType) {
                                    case 'text':
                                    case 'decimal':
                                    case 'percent':
                                    case 'bool':
                                    case 'currency':
                                    case 'integer':
                                    case 'date':
                                        field.value =
                                            value?.value !== undefined ? value.value : value;
                                        break;
                                    case 'relatedValueList':
                                    case 'singleValueList':
                                    case 'fuzzySearchSingle':
                                    case 'multipleValueList':
                                    case 'fuzzySearchMultiple':
                                        field.value = value?.value;
                                        field.label = value?.description;
                                        break;
                                }

                                wrapperSection.data.push(field);
                            });
                            extraFields.push(wrapperSection);
                        });
                        entity.extraFields = extraFields;
                        success({ entity, data });
                    },
                    error,
                );
            },
            error,
        );
    }

    getOrderProducts(id, success, error) {
        this.context.domainManager.getOrderProducts(id, (data) => success(data.Result), error);
    }

    getSalesOrdersProductLines(idFolder, searchText, idRate, idCompany, idEntorno) {
        return new Promise((resolve, reject) => {
            this.context.domainManager.getSalesOrderProducts(
                0,
                500,
                idFolder,
                searchText,
                idRate?.value || idRate,
                [],
                idCompany?.value || idCompany,
                idEntorno,
                (result) => {
                    const groups = result?.Result?.reduce((groups, product) => {
                        if (!groups[product.idFolder]) {
                            groups[product.idFolder] = {
                                id: product.idFolder,
                                pathId: product.pathId,
                                path: product.path,
                                products: [],
                            };
                        }
                        groups[product.idFolder].products.push(product);
                        return groups;
                    }, {});
                    resolve(
                        Object.keys(groups)
                            .map((key) => groups[key])
                            .sort((a, b) => Number(a.id) - Number(b.id)),
                    );
                },
                reject,
            );
        });
    }

    getSalesOrderProducts(idFolder, searchText, idRate, idCompany, idEntorno) {
        return new Promise((resolve, reject) => {
            this.context.domainManager.getSalesOrderProducts(
                0,
                500,
                idFolder,
                searchText,
                idRate?.value || idRate,
                [],
                idCompany?.value || idCompany,
                idEntorno,
                (result) => {
                    resolve(result.Result);
                },
                reject,
            );
        });
    }

    getSalesOrderModalProducts(
        idFolder,
        searchText,
        idRate,
        idCompany,
        idEntorno,
        filters = {},
        init = 0,
        count = 50,
    ) {
        const filtersWithAvailable = {
            ...filters,
            useAvailableFilterProducts: { value: '1', dataType: 'text' },
        };
        return this.getSalesOrderProductsWithAdvancedFilters(
            idFolder,
            searchText,
            idRate,
            idCompany,
            idEntorno,
            filtersWithAvailable,
            init,
            count,
        );
    }

    /**
     * Get total count of products for the modal using the standard /count endpoint
     */
    getSalesOrderModalProductsCount(idFolder, searchText, idEntorno, filters = {}) {
        const filtersWithAvailable = {
            ...filters,
            useAvailableFilterProducts: { value: '1', dataType: 'text' },
        };
        const filtersWithEnvironment = this._getFiltersWithEnvironment(
            filtersWithAvailable,
            idEntorno,
        );

        // Add folder and search to filters for the count endpoint
        const filtersForCount = {
            ...filtersWithEnvironment,
        };

        // Add folder filter if not root
        if (idFolder && idFolder !== -1 && idFolder !== '-1') {
            filtersForCount.idProductFamily = {
                value: String(idFolder),
                byParameter: true,
            };
        }

        // Add search filter if present
        if (searchText) {
            filtersForCount.matchingName = {
                value: searchText,
            };
        }

        // Format filters for the count endpoint using the same format as other entities
        const formattedFilters = formatSelectionFilters(filtersForCount, PRODUCTS);

        const countParams = {
            filter: formattedFilters,
        };

        return this.context.entityListManager.getEntityCounts(PRODUCTS.countName, countParams);
    }

    /**
     * Helper to add environment filter
     */
    _getFiltersWithEnvironment(filters, idEntorno) {
        const filtersWithEnvironment = { ...filters };
        const envId = String(idEntorno);
        if (idEntorno && envId !== '-1' && envId !== '') {
            filtersWithEnvironment.environment = {
                value: [{ value: envId }],
                dataType: 'multipleValueList',
            };
        }
        return filtersWithEnvironment;
    }

    async getSalesOrderProductsWithAdvancedFilters(
        idFolder,
        searchText,
        idRate,
        idCompany,
        idEntorno,
        filters,
        init = 0,
        count = 50,
    ) {
        const filtersWithEnvironment = this._getFiltersWithEnvironment(filters, idEntorno);

        // First call: Get paginated product IDs from /sfm endpoint
        const sfmResult = await this.context.domainManager.getProductsSfmWithFilters(
            init,
            count,
            idFolder,
            searchText,
            filtersWithEnvironment,
        );

        const productsArray = Array.isArray(sfmResult) ? sfmResult : sfmResult?.Data || [];
        const total = sfmResult?.TotalRows || sfmResult?.Total || productsArray.length;
        const filteredProductIds = productsArray.map((p) => p.Id || p.id);

        if (filteredProductIds.length === 0) {
            return { products: [], total };
        }

        // Create order map before second call for better performance
        const orderMap = new Map(filteredProductIds.map((id, index) => [String(id), index]));

        // Build productTypeCode map from sfm results (not available in salesOrders/products)
        const productTypeCodeMap = new Map(
            productsArray.map((p) => [
                String(p.Id ?? p.id),
                p.productTypeCode ?? p.ProductTypeCode,
            ]),
        );

        // Second call: Get products with rate-specific pricing (only for current page IDs)
        const result = await new Promise((resolve, reject) => {
            this.context.domainManager.getSalesOrderProducts(
                0,
                filteredProductIds.length,
                idFolder,
                searchText,
                idRate?.value || idRate,
                filteredProductIds.map((id) => ({ idproducto: id })),
                idCompany?.value || idCompany,
                idEntorno,
                resolve,
                reject,
            );
        });

        // Reorder results to match the order from products/sfm endpoint
        // and enrich each product with productTypeCode from the sfm data
        const products = [...(result.Result || [])]
            .sort((a, b) => {
                const indexA = orderMap.get(String(a.Id ?? a.id)) ?? Infinity;
                const indexB = orderMap.get(String(b.Id ?? b.id)) ?? Infinity;
                return indexA - indexB;
            })
            .map((product) => {
                const id = String(product.Id ?? product.id);
                const productTypeCode = productTypeCodeMap.get(id);
                return productTypeCode !== undefined ? { ...product, productTypeCode } : product;
            });

        return { products, total };
    }

    calculateEntityProducts(data, mode) {
        const state = Context.store.getState();
        const useTaxesSalesOrders = state?.config?.userData?.useTaxesSalesOrders || false;
        const contextData = this.getLineContextData(data);
        let subtotal = 0;
        let subtotalAmount = 0;
        let taxableBaseAmount = 0;
        let taxTotalAmount = 0;
        const rawProducts = data.products ? [...data.products] : [];
        const products =
            data.products?.map((product, index) => {
                const parsedProduct = this.parseProduct(
                    product,
                    index,
                    data.currencySymbol,
                    mode,
                    data,
                );
                const lineGrossSubtotal = this.parseDecimalValue(
                    parsedProduct.subtotalAmount ??
                        this.parseDecimalValue(parsedProduct.quantity) *
                            this.getLineEffectiveUnitPrice(parsedProduct),
                );
                const lineTaxBase = this.parseDecimalValue(parsedProduct.finalprice);

                // Legacy subtotal/total pair kept for non-taxes mode:
                // subtotal = sum of line net bases (after line discounts).
                subtotal += lineTaxBase;
                subtotalAmount += lineGrossSubtotal;

                return parsedProduct;
            }) || [];

        const discount = this.calculateTotalDiscount(
            data.discount1,
            data.discount2,
            data.discount3,
            data.discount4,
        );
        const total = subtotal * discount;
        let discountTotalAmount = null;
        let finalTotalAmount = null;

        if (useTaxesSalesOrders) {
            // Single source of truth for taxes-enabled totals in list/detail/crud views.
            const totals = this.calculateTaxedDocumentTotals(products, data, state, true);
            subtotalAmount = totals.subtotalAmount;
            discountTotalAmount = totals.discountTotalAmount;
            taxableBaseAmount = totals.taxableBaseAmount;
            taxTotalAmount = totals.taxTotalAmount;
            finalTotalAmount = totals.finalTotalAmount;
        } else {
            // Legacy (no document taxes): same header semantics as calculateTaxedDocumentTotals —
            // gross subtotal, discount = gross − net after header discounts, base and final = net total.
            const roundedSubtotalAmount = this.roundSalesOrderAmount(
                subtotalAmount,
                'subtotalAmount',
                contextData,
            );
            const roundedTaxableBaseAmount = this.roundSalesOrderAmount(
                total,
                'taxableBaseAmount',
                contextData,
            );
            const roundedFinalTotalAmount = this.roundSalesOrderAmount(
                total,
                'finalTotalAmount',
                contextData,
            );
            const roundedDiscountTotalAmount = this.roundSalesOrderAmount(
                this.parseDecimalValue(roundedSubtotalAmount - roundedTaxableBaseAmount),
                'discountTotalAmount',
                contextData,
            );
            subtotalAmount = roundedSubtotalAmount;
            discountTotalAmount = roundedDiscountTotalAmount;
            taxableBaseAmount = roundedTaxableBaseAmount;
            taxTotalAmount = this.roundSalesOrderAmount(0, 'taxTotalAmount', contextData);
            finalTotalAmount = roundedFinalTotalAmount;
        }

        let shippingCost = null;
        const salesOrderShippingCost = !!state?.config?.userData?.salesOrderShippingCost;
        if (salesOrderShippingCost) {
            shippingCost = this.calculateShippingCost(data, products);
        }

        return {
            rawProducts,
            products,
            subtotal,
            total,
            subtotalAmount,
            discountTotalAmount,
            taxableBaseAmount,
            taxTotalAmount,
            finalTotalAmount,
            shippingCost,
        };
    }

    parseProduct(product, index, currencySymbol, mode, salesOrderData = {}) {
        const state = Context.store.getState();
        const useTaxesSalesOrders = state?.config?.userData?.useTaxesSalesOrders || false;
        const firstImageId = product.imageList ? product.imageList.split(';')[0] : null;
        const productImageUrl = getSrcProductCircleAvatar(firstImageId);

        const imageListUrls =
            product.imageList?.split(';').reduce((arr, id) => {
                const { src, fallbackSrc } = getSrcProductCircleAvatar(id);
                arr.push({ src, fallbackSrc });
                return arr;
            }, []) || null;

        const nestedExtraFields =
            product.extraFields &&
            typeof product.extraFields === 'object' &&
            !Array.isArray(product.extraFields)
                ? product.extraFields
                : {};
        const productFields = { ...nestedExtraFields, ...product };

        const flattenExtraFields = Object.keys(productFields).reduce((newObj, key) => {
            if (key === 'extraFields') return newObj;

            const field = productFields[key];
            const extraFieldConfig = getEntityExtraFieldSchemaConfig(
                SALESORDERSLINES.extraFieldName,
                key,
            );

            if (extraFieldConfig?.dataType === 'date') {
                const includeTime = getBackendBoolean(extraFieldConfig?.includeTime);
                if (mode === 'detail') {
                    newObj[key] = formatLineDateForDisplay(field, { includeTime });
                } else if (mode === 'edit') {
                    newObj[key] = normalizeLineDateValue(
                        field?.hasOwnProperty?.('value')
                            ? (field.value ?? field.description)
                            : field,
                    );
                }
                return newObj;
            }

            if (field?.hasOwnProperty('value') && field?.hasOwnProperty('description')) {
                const valueStr = String(field.value ?? '');
                const ids = valueStr.split(';').filter(Boolean);
                if (ids.length > 0 && valueStr.includes(';')) {
                    const labels = String(field.description)
                        .split(';')
                        .map((label) => label.trim())
                        .filter(Boolean);
                    if (mode === 'detail') {
                        newObj[key] = labels.length ? labels : ids;
                    } else if (mode === 'edit') {
                        newObj[key] = ids.map((id, index) => ({
                            label: labels[index] || id,
                            value: String(id),
                        }));
                    }
                } else if (mode === 'detail') newObj[key] = field.description;
                else if (mode === 'edit')
                    newObj[key] = { label: field.description, value: String(field.value) };
            } else if (field?.hasOwnProperty('value')) {
                newObj[key] = field.value;
            } else newObj[key] = field;

            return newObj;
        }, {});

        Object.keys(flattenExtraFields).forEach((key) => {
            const lowerKey = key.toLowerCase();
            if (lowerKey.startsWith('z_') && flattenExtraFields[lowerKey] === undefined) {
                flattenExtraFields[lowerKey] = flattenExtraFields[key];
            }
        });

        let parsedProduct = {
            rawData: product,
            ...flattenExtraFields,
            ...(product._lineSmartFieldOverrides
                ? { _lineSmartFieldOverrides: product._lineSmartFieldOverrides }
                : {}),
            numLine: index + 1,
            currencySymbol: currencySymbol,
            rowImageUrl: productImageUrl.src,
            rowImageUrlFallback: productImageUrl.fallbackSrc,
            imageListUrls,
            discount1: product.discount1 || 0,
            discount2: product.discount2 || 0,
            discount3: product.discount3 || 0,
            discount4: product.discount4 || 0,
            // Original product unit price: backend returns it alongside the line;
            // used as the price reset target when the UoM relation changes.
            productPrice: product.productPrice ?? product.price ?? null,
        };

        // UoM hydration: preserve backend-provided scalar UoM and product relations,
        // and memoize unitOfMeasureRelation so getLineEffectiveUnitPrice resolves in O(1).
        if (state?.config?.userData?.uomEnabled) {
            const productUnitRelations = product.productUnitRelations || [];
            parsedProduct.productUnitRelations = productUnitRelations;
            parsedProduct.idRelationUoM = nullIfBackendEmptyId(product.idRelationUoM);
            const hasAssignedUom = parsedProduct.idRelationUoM != null;
            parsedProduct.unitOfMeasure = hasAssignedUom ? product.unitOfMeasure || null : null;
            parsedProduct.unitOfMeasureRelation = hasAssignedUom
                ? findProductUnitRelationByStoredUomField(
                      productUnitRelations,
                      parsedProduct.idRelationUoM,
                  ) || null
                : null;
        }

        if (this.salesOrderAmountDiscountsEnabled(state)) {
            for (let i = 1; i <= 4; i += 1) {
                const camel = `discount${i}Value`;
                const lower = `discount${i}value`;
                if (parsedProduct[lower] !== undefined && parsedProduct[camel] === undefined) {
                    parsedProduct[camel] = parsedProduct[lower];
                }
                delete parsedProduct[lower];
            }
        }

        if (useTaxesSalesOrders) {
            parsedProduct = this.recalculateProductLine(
                parsedProduct,
                {
                    ...salesOrderData,
                    idBranch: salesOrderData.idBranch ?? parsedProduct.idBranch ?? product.idBranch,
                    idCurrency:
                        salesOrderData.idCurrency ?? parsedProduct.idCurrency ?? product.idCurrency,
                },
                state,
                { fillMissingOnly: true },
            );

            if (mode === 'detail') {
                parsedProduct.taxRateId = parsedProduct.taxRate;
            } else if (mode === 'edit') {
                // Keep taxRateId=0 as a valid option (do not treat as empty).
                const normalizedId = this.normalizeTaxRateId(parsedProduct.taxRateId);
                parsedProduct.taxRateId =
                    normalizedId !== null
                        ? {
                              label: parsedProduct.taxRate || '',
                              value: String(normalizedId),
                          }
                        : null;
            }
        } else if (this.salesOrderAmountDiscountsEnabled(state)) {
            parsedProduct = this.recalculateProductLine(
                parsedProduct,
                {
                    ...salesOrderData,
                    idBranch: salesOrderData.idBranch ?? parsedProduct.idBranch ?? product.idBranch,
                    idCurrency:
                        salesOrderData.idCurrency ?? parsedProduct.idCurrency ?? product.idCurrency,
                },
                state,
                { fillMissingOnly: true },
            );
        }

        return parsedProduct;
    }

    getExtraClassNameStateValue(option) {
        if (option.win === 'True' && option.lost === 'False') {
            return 'salesorders-state-win';
        }
        if (option.win === 'False' && option.lost === 'True') {
            return 'salesorders-state-lost';
        }
    }

    updateEntity(id, entity, success, error) {
        const config = this.context.cacheManager.getConfigStore();
        this.getCrudSchema(
            config,
            SALESORDERS,
            (schema) => {
                const crud = SalesOrdersModel.fromCrud(entity);
                this.context.domainManager.updateEntity(
                    SALESORDERS.entity,
                    id,
                    crud.createCrud(schema),
                    (response) => {
                        successToast({
                            text: getLiteral('success_salesorders_entity_updated'),
                        });
                        success(response);
                    },
                    error,
                );
            },
            error,
        );
    }

    createEntity(entity, success, error) {
        const config = this.context.cacheManager.getConfigStore();
        this.getCrudSchema(
            config,
            SALESORDERS,
            (schema) => {
                const crud = SalesOrdersModel.fromCrud(entity);
                this.context.domainManager.createEntity(
                    SALESORDERS.entity,
                    crud.createCrud(schema),
                    (response) => {
                        successToast({
                            text: getLiteral('success_salesorders_entity_created'),
                        });
                        success(response);
                    },
                    error,
                );
            },
            error,
        );
    }

    refreshData(isDeleted, id) {
        if (isDeleted) {
            successToast({
                text: getLiteral('success_salesorders_entity_deleted'),
            });
        }
        this.context.store.dispatch(this.context.actions.EntityListActions.init(SALESORDERS, true));
    }

    getOrders(init, count, filter, success, error) {
        this.context.domainManager.getOrders(
            init,
            count,
            filter,
            '',
            -1,
            (data) => {
                success(data.Result ? data.Result : [], data.TotalRows, init);
            },
            error,
        );
    }

    setFollow(orderId, follow, isShared = false, success, error) {
        this.context.domainManager.setFollow(
            SALESORDERS.entity,
            orderId,
            follow,
            isShared,
            success,
            error,
        );
    }

    getErrors(schema, entityCrud, dynamic) {
        const errorsObject = getLegacyFormErrors(schema.form, entityCrud, dynamic);
        const legacyErrors = errorsObject.errors;
        let errors = legacyErrors?.toObject
            ? { ...legacyErrors.toObject() }
            : { ...(legacyErrors || {}) };

        const model = SalesOrdersModel.fromCrud(entityCrud);
        const errorProducts = {};
        model.products.forEach((salesOrderProductModel, index) => {
            const errorProduct = {};
            schema.products.forEach((schemaProduct) => {
                if (
                    schemaProduct.mandatory &&
                    isEmpty(
                        salesOrderProductModel.entity[schemaProduct.id.toLowerCase()],
                        schemaProduct.dataType,
                    )
                ) {
                    errorProduct[schemaProduct.id.toLowerCase()] = '  ';
                }
            });
            if (Object.keys(errorProduct).length > 0) {
                errorProducts[index] = errorProduct;
            }
        });
        if (Object.keys(errorProducts).length > 0) {
            errors.products = errorProducts;
        }

        errorsObject.errors = errors;

        return errorsObject;
    }

    /*
    TO DEPRECATE
     */
    setReportsForm(reportSignedFormModel, success, error) {
        this.context.domainManager.setReportsForm(
            SALESORDERS.entity,
            reportSignedFormModel,
            (result) => {
                success(result);
            },
            error,
        );
    }

    setAsPrincipal(id, principal, success) {
        this.context.domainManager.setAsPrincipal(
            id,
            principal,
            () => {
                success();
            },
            () => {},
        );
    }

    getFilterSchema = () => {
        const state = Context.store.getState();
        const currenUser = `${state.config.userData.nombre} ${state.config.userData.apellidos}`;

        return [
            {
                id: 'products',
                dataType: 'multipleValueList',
                description: getLiteral('label_product'),
                inputAttrs: {
                    ...FuzzyMap.productos,
                    isMulti: true,
                },
                extraFilterProps: {
                    id: 'productsQ',
                    dataType: 'multipleValueList',
                    description: getLiteral('label_product_quantity'),
                    isExtraFilter: true,
                    isMulti: true,
                },
                hint: getLiteral('action_searchproduct'),
                asExtra: false,
                beforeRenderValueList: this.beforeRenderValueList,
                overrideDefaultComponent: 'Products',
            },
            {
                id: 'productCategories',
                dataType: 'singleValueList',
                description: getLiteral('label_productcategory'),
                inputAttrs: {
                    list: 'productcategory',
                },
                hint: getLiteral('action_select'),
            },
            {
                id: 'productFamilies',
                dataType: 'singleValueList',
                description: getLiteral('label_product_family'),
                inputAttrs: {
                    ...FuzzyMap.productFamilies,
                },
                hint: getLiteral('action_select'),
            },
            {
                id: 'rates',
                dataType: 'singleValueList',
                description: getLiteral('label_rate'),
                fieldConfiguration: 'idTarifa',
                inputAttrs: {
                    list: 'tblTarifas',
                },
                hint: getLiteral('action_select'),
            },
            {
                id: 'states',
                dataType: 'singleValueList',
                description: getLiteral('label_status_salesorders'),
                fieldConfiguration: 'idEstadoSalesOrder',
                getExtraClassNameValue: this.getExtraClassNameStateValue,
                inputAttrs: {
                    list: 'tblEstadosSalesOrders',
                    filterOption: this.filterOptionStatesByType,
                },
                hint: getLiteral('action_select'),
            },
            {
                id: 'companies',
                dataType: 'singleValueList',
                description: getLiteral('label_account'),
                fieldConfiguration: 'idEmpresa',
                inputAttrs: {
                    ...FuzzyMap.empresas,
                },
                hint: getLiteral('action_searchcompany'),
            },
            {
                id: 'opportunities',
                dataType: 'singleValueList',
                description: getLiteral('title_opportunity'),
                fieldConfiguration: 'idExpediente',
                inputAttrs: {
                    ...FuzzyMap.expedientes,
                },
                hint: getLiteral('action_searchopportunity'),
            },
            {
                id: 'responsibles',
                dataType: 'singleValueList',
                description: getLiteral('label_owner'),
                fieldConfiguration: 'idComercial',
                inputAttrs: {
                    ...FuzzyMap.viewusuariosfullname,
                    defaultSearch: currenUser,
                },
                hint: getLiteral('action_searchowner'),
            },
            {
                id: 'environment',
                fieldConfiguration: 'idSucursal',
                dataType: 'singleValueList',
                description: getLiteral('label_environment'),
                inputAttrs: {
                    list: 'tblSucursales',
                },
                hint: getLiteral('action_searchowner'),
            },
            {
                id: 'amount',
                serverKeys: { from: 'amountMin', to: 'amountMax' },
                dataType: 'currency',
                description: getLiteral('label_total'),
                fieldConfiguration: 'totalAmount',
            },
            {
                id: 'date',
                serverKeys: { from: 'dateMin', to: 'dateMax' },
                dataType: 'date',
                description: getLiteral('label_valid_until'),
                fieldConfiguration: 'expectedCloseDate',
                locators: {
                    first: 'orders-filter__date-start',
                    last: 'orders-filter__date-end',
                },
            },
            {
                id: 'closeDate',
                serverKeys: { from: 'closeDateFrom', to: 'closeDateTo' },
                dataType: 'date',
                description: getLiteral('label_salesorders_close'),
                fieldConfiguration: 'CloseDate',
                locators: {
                    first: 'orders-filter__closing-date-start',
                    last: 'orders-filter__closing-date-end',
                },
            },
            {
                id: 'fcreado',
                description: getLiteral('label_created'),
                dataType: 'date',
                asExtra: true,
                isAudit: true,
                locators: {
                    first: 'orders-filter__creation-date-start',
                    last: 'orders-filter__creation-date-end',
                },
            },
            {
                id: 'extid',
                fieldConfiguration: 'extid',
                description: getLiteral('label_extid'),
                dataType: 'text',
                asExtra: true,
            },
            {
                id: 'followingItem',
                dataType: 'singleValueList',
                description: getLiteral('label_following'),
                generateOptions: () => {
                    return [
                        { label: getLiteral('label_following'), value: '1' },
                        { label: getLiteral('label_not_following'), value: '2' },
                    ];
                },
            },
            {
                id: 'salesOrderType',
                fieldConfiguration: 'salesOrderType',
                dataType: 'singleValueList',
                description: getLiteral('label_salesorder_type'),
                hint: getLiteral('label_selectone'),
                inputAttrs: {
                    list: 'tblTiposSalesOrders',
                },
                showOnlyAsCrossFilter: true,
                byParameter: state.config?.userData?.enableSalesOrdersTypes,
            },
            getBackendBoolean(state?.config?.userData?.syncStatusEnabled || false) &&
                SALESORDERS.syncWithSageEnabled && {
                    id: 'syncStatusId',
                    dataType: 'singleValueList',
                    fieldConfiguration: 'syncStatusId',
                    description: getLiteral('label_connector_sync_status'),
                    inputAttrs: {
                        list: 'tblSyncStatuses',
                    },
                    avoidListAsObject: true,
                },
            // Below are the filters related to the cross filters functionality of this entity
            // the one tagged as "isFakeCrossFilter" is to be shown as cross filter in the allowed entities
            // the ones tagged as "invisible" are standard filters of this entity that will be set from the
            // cross filters section of the matching entity (because UX & BE)
            {
                id: 'hasOrder',
                description: getLiteral('label_filters_cross_filters_has_salesorder'),
                dataType: 'bool',
                isFakeCrossFilter: true,
            },
            {
                id: 'hasAccount',
                dataType: 'bool',
                invisible: true,
            },
            {
                id: 'hasContact',
                dataType: 'bool',
                invisible: true,
            },
            {
                id: 'hasOpportunity',
                dataType: 'bool',
                invisible: true,
            },
        ].filter(Boolean);
    };

    filterOptionStatesByType(option) {
        const state = Context.store.getState();
        const filters = state?.entityFilters?.[SALESORDERS.entity]?.filters;
        const pipelineSelected = filters?.salesOrderType;

        const hasDependency =
            state.config?.standardFieldsSchemaMap?.SALESORDERS?.idEstadoSalesOrder
                ?.strParentField === 'salesOrderType';

        if (pipelineSelected && hasDependency) {
            const pipelineId = pipelineSelected.value[0]?.value;
            const idparent = option?.idparent || option?.data?.idparent;
            return idparent === pipelineId;
        }

        return true;
    }

    getSpecificFilterToPreFilter = (id) => {
        let schema = this.getFilterSchema();
        let field = schema.find((f) => {
            return f.id === id;
        });
        return field;
    };

    transformDataForEntityList = (backendData) => {
        let totalRows = backendData.TotalRows;
        if (backendData.State === '1' && totalRows === -1) {
            totalRows = 0;
        }
        return {
            Data: backendData.Result,
            MetaData: { totalRows: totalRows },
        };
    };

    getRateCaducityError = ({ value }) => {
        if (!value) return '';
        const state = Context.store.getState();
        const rates = state?.serverList?.ratesValueList?.data;
        const visibleRate = this.filterTblRates(rates)?.find(
            (rate) => Number(rate.id) === Number(value?.value || value),
        );

        return !visibleRate ? getLiteral('label_expired_rate') : '';
    };

    getCrudPreComponents = () => {
        const state = Context.store.getState();
        const activeCrud = getActiveCrud(state);

        if (!activeCrud?.id) {
            return Promise.resolve([]);
        }

        const permission = state?.config?.permission;
        const entityDetail = state?.entityDetail;
        const activeDetailKey = entityDetail?.active;
        const activeDetailData = activeDetailKey ? entityDetail[activeDetailKey] : null;
        const parentEntityData = activeDetailData?.data;

        const shouldShowWarning =
            parentEntityData?.warningText &&
            permission?.warnings &&
            (permission?.editWarnings || permission?.viewWarningsInSalesOrders);

        if (!shouldShowWarning) {
            return Promise.resolve([]);
        }

        const info = {
            component: 'advice',
            componentProps: {
                type: 'info',
                showIcon: true,
                showCollapse: true,
            },
            content: parentEntityData.warningText,
        };

        return Promise.resolve([info]);
    };

    postDuplication = (data) => {
        return new Promise((resolve, reject) => {
            const state = Context.store.getState();
            const config = state.config;
            const salesOrderManualPrices = state?.config?.userData?.salesOrderManualPrices;

            data.idStateSalesOrder =
                config?.standardFieldsSchemaMap?.SALESORDERS?.idEstadoSalesOrder?.defaultValue;

            data.idResponsible = config.userData.idUsuario;
            data.responsible = `${config.userData.nombre} ${config.userData.apellidos}`;

            if (Array.isArray(data.extraFields)) {
                const newExtraFields = {};
                data.extraFields.forEach((tab) =>
                    tab.data.forEach((field) => (newExtraFields[field.id] = field.value)),
                );
                data.extraFields = newExtraFields;
            }

            const convertProductsToOrderslines = (products) => {
                if (!products?.length) return;
                const useTaxesSalesOrders =
                    Context.store.getState()?.config?.userData?.useTaxesSalesOrders || false;

                const salesOrdersLinesSchema =
                    config?.extraFieldsSchemaMap?.[SALESORDERSLINES.extraFieldName] || {};

                const schemaKeysMap = {};
                Object.keys(salesOrdersLinesSchema).forEach((schemaKey) => {
                    schemaKeysMap[schemaKey.toLowerCase()] = schemaKey;
                });

                data.orderslines = products.map((product, index) => {
                    const extraFields = {};
                    const extraFieldsProduct = data.rawProducts?.[index] || product;
                    if (extraFieldsProduct) {
                        Object.keys(extraFieldsProduct).forEach((key) => {
                            const schemaKey = schemaKeysMap[key.toLowerCase()];
                            if (
                                schemaKey &&
                                extraFieldsProduct[key] !== undefined &&
                                extraFieldsProduct[key] !== null
                            ) {
                                extraFields[schemaKey] =
                                    extraFieldsProduct[key]?.value !== undefined
                                        ? extraFieldsProduct[key].value
                                        : extraFieldsProduct[key];
                            }
                        });
                    }

                    const orderLine = {
                        idproduct: product.idproduct,
                        description: product.description,
                        quantity: product.quantity,
                        price: product.price,
                        discount1: product.discount1,
                        discount2: product.discount2,
                        discount3: product.discount3,
                        discount4: product.discount4,
                        finalprice: product.finalprice,
                        extraFields: extraFields,
                    };

                    if (useTaxesSalesOrders) {
                        orderLine.subtotalAmount = product.subtotalAmount;
                        // Ensure duplicated orders also use deterministic tax-rate fallback
                        // (0% option) when original line has no taxRateId.
                        orderLine.taxRateId = this.resolveTaxRateId(product.taxRateId, state);
                        orderLine.totalAmount = product.finalprice;
                    }

                    return orderLine;
                });

                delete data.products;
                data.amountSalesOrder = data.totalRaw;
                if (useTaxesSalesOrders) {
                    // Same canonical totals calculation used by CRUD save flow.
                    const totals = this.calculateTaxedDocumentTotals(
                        data.orderslines,
                        data,
                        state,
                        true,
                    );

                    data.subtotalAmount = data.subtotalAmount ?? totals.subtotalAmount;
                    data.taxableBaseAmount = data.taxableBaseAmount ?? totals.taxableBaseAmount;
                    data.discountTotalAmount =
                        data.discountTotalAmount ?? totals.discountTotalAmount;
                    data.taxTotalAmount = data.taxTotalAmount ?? totals.taxTotalAmount;
                    data.finalTotalAmount = data.finalTotalAmount ?? totals.finalTotalAmount;
                    data.amountSalesOrder = data.finalTotalAmount;
                } else {
                    delete data.subtotalAmount;
                    delete data.discountTotalAmount;
                    delete data.taxableBaseAmount;
                    delete data.taxTotalAmount;
                    delete data.finalTotalAmount;
                }
                delete data.totalRaw;
                delete data.subtotalRaw;
            };

            if (!data.products?.length && data.orderslines?.length) {
                data.orderslines = data.orderslines.map((orderline) => {
                    const { entityId, ...cleanOrderline } = orderline;
                    return cleanOrderline;
                });
                resolve(data);
                return;
            }

            if (!data.products?.length || !data.idRate) {
                convertProductsToOrderslines(data.products);
                resolve(data);
                return;
            }

            let rates = state?.serverList?.ratesValueList?.data;
            const validRates = this.filterTblRates(rates);
            const idRate = Number(data.idRate?.value || data.idRate);
            const currentRate = validRates.find((rate) => Number(rate.value) === idRate);

            if (currentRate || salesOrderManualPrices || !idRate || idRate === -1) {
                convertProductsToOrderslines(data.products);
                resolve(data);
                return;
            }

            // Following without rate because not valid
            data.idRate = null;
            data.rate = null;
            const fields = { idRate: null };

            const dispatchChanges = (changes) => {
                data = { ...data, ...changes };

                const productsToMap =
                    changes.products?.length > 0 ? changes.products : data.products;

                convertProductsToOrderslines(productsToMap);

                if (changes.totalRaw !== undefined) {
                    data.amountSalesOrder = changes.totalRaw;
                }

                return data;
            };

            this.postChangeFields({
                fields,
                data,
                dispatchChanges,
                state,
            })
                .then((newData = {}) => resolve({ ...data, ...newData }))
                .catch(reject);
        });
    };

    whileInitCrud = (data) => {
        const state = Context.store.getState();
        const enableSalesOrdersTypes = state.config.userData.enableSalesOrdersTypes;
        const salesOrderType = state.entityFilters[SALESORDERS.entity]?.filters?.salesOrderType;

        if (!enableSalesOrdersTypes) return;

        return Context.serverListManager
            .getList('tblTiposSalesOrders')
            .then((result) => {
                const currentType = salesOrderType?.value?.[0] || result?.[0];
                const defaultType = result?.find(
                    (type) =>
                        String(type.value) ===
                        String(
                            state.config?.standardFieldsSchemaMap?.SALESORDERS?.salesOrderType
                                ?.defaultValue,
                        ),
                );

                if (!data.salesOrderType) {
                    if (defaultType) data.salesOrderType = defaultType?.value;
                    else if (currentType) data.salesOrderType = currentType?.value;
                }
                return data;
            })
            .catch((error) => {
                console.error(error);
            });
    };

    formatFiltersToSend = (filters) => {
        const newFilters = translateFollowingItemForBackend(filters, SALESORDERS);
        return newFilters;
    };
}
